import React, { useState, useEffect, useRef } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CalendarTaskModal from './CalendarTaskModal';
import EditLayoutModal from '../layout/EditLayoutModal';
import DataApi from '../../api/DataApi';
import jwt_decode from 'jwt-decode';
import outlookApi from '../../api/OutlookIntegrationApi';
import googleCalendarApi from '../../api/GoogleCalendarApi';

const localizer = momentLocalizer(moment);

const CalendarComponent = () => {
  const [currentDate, setCurrentDate] = useState(moment());
  const [events, setEvents] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState(Views.MONTH);
  const [showWeekends, setShowWeekends] = useState(true);
  const [showDeclinedEvents, setShowDeclinedEvents] = useState(true);
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [searchQuery, setSearchQuery] = useState('');
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [outlookEvents, setOutlookEvents] = useState([]);
  const [showOutlookEvents, setShowOutlookEvents] = useState(true);
  const [outlookSyncStatus, setOutlookSyncStatus] = useState('');
  const [isSyncingOutlook, setIsSyncingOutlook] = useState(false);
  const [outlookUserInfo, setOutlookUserInfo] = useState(null);
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [googleSyncStatus, setGoogleSyncStatus] = useState('');
  const [isSyncingGoogle, setIsSyncingGoogle] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [showGoogleEvents, setShowGoogleEvents] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [selectedOutlookAccount, setSelectedOutlookAccount] = useState(null);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showEditLayoutModal, setShowEditLayoutModal] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  const [crmTasks, setCrmTasks] = useState([]);
  const [isFetchingTasks, setIsFetchingTasks] = useState(false);
  const [hasMailConfig, setHasMailConfig] = useState(false);

  const getCurrentUser = () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwt_decode(token);
        return {
          id: decoded.id,
          firstname: decoded.firstname,
          lastname: decoded.lastname,
          email: decoded.email,
          username: decoded.username,
        };
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return null;
  };

  const checkAndSyncMailConfig = async () => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.email) {
        console.log('No user email found');
        return { hasConfig: false, type: null };
      }

      try {
        const outlookResponse = await fetch(
          'http://localhost:9022/api/outlook/config',
          {
            headers: {
              Authorization: localStorage.getItem('token'),
            },
          }
        );

        if (outlookResponse.ok) {
          const outlookData = await outlookResponse.json();
          if (outlookData.success && outlookData.config) {
            console.log(' Outlook config found for user, syncing...');
            setHasMailConfig(true);
            setShowOutlookEvents(true);
            setShowGoogleEvents(false);

            fetchOutlookEvents();

            return { hasConfig: true, type: 'outlook' };
          }
        }
        console.log(' No Outlook config found, checking Google...');
      } catch (outlookError) {
        console.log(' Outlook config error:', outlookError.message);
      }
      try {
        const googleResponse = await fetch(
          'http://localhost:9022/api/google-calendar/config',
          {
            headers: {
              Authorization: localStorage.getItem('token'),
            },
          }
        );

        console.log('Google config response status:', googleResponse.status);

        if (googleResponse.ok) {
          const googleData = await googleResponse.json();
          console.log('Google config data:', googleData);

          if (googleData.success && googleData.config) {
            setHasMailConfig(true);
            setShowGoogleEvents(true);
            setShowOutlookEvents(false);
            const dbAccessToken = googleData.config.GOOGLE_ACCESS_TOKEN;
            const dbRefreshToken = googleData.config.GOOGLE_REFRESH_TOKEN;
            const localAccessToken = localStorage.getItem('googleAccessToken');
            const localRefreshToken =
              localStorage.getItem('googleRefreshToken');

            if (dbAccessToken && dbRefreshToken) {
              localStorage.setItem('googleAccessToken', dbAccessToken);
              localStorage.setItem('googleRefreshToken', dbRefreshToken);
              setGoogleAccessToken(dbAccessToken);

              setTimeout(() => {
                handleSyncGoogle();
              }, 500);
            } else if (localAccessToken && localRefreshToken) {
              setGoogleAccessToken(localAccessToken);
              setTimeout(() => {
                handleSyncGoogle();
              }, 500);
            } else {
            }

            return { hasConfig: true, type: 'google' };
          }
        }
      } catch (googleError) { }

      setHasMailConfig(false);
      setShowOutlookEvents(false);
      setShowGoogleEvents(false);
      return { hasConfig: false, type: null };
    } catch (error) {
      console.error('Error checking mail config:', error);
      setHasMailConfig(false);
      return { hasConfig: false, type: null };
    }
  };

  const isDuplicateEvent = (crmTask, externalEvent) => {
    if (!crmTask || !externalEvent) return false;

    const crmTitle = (crmTask.title || '').toLowerCase().trim();
    const extTitle = (externalEvent.title || '').toLowerCase().trim();
    const titleMatch =
      crmTitle === extTitle ||
      crmTitle.includes(extTitle) ||
      extTitle.includes(crmTitle);

    const crmStart = moment(crmTask.meeting_start_datetime || crmTask.due_date);
    const extStart = moment(externalEvent.start);
    const timeDiff = Math.abs(crmStart.diff(extStart, 'minutes'));
    const timeMatch = timeDiff <= 60; // Within 1 hour

    const isDuplicate = titleMatch && timeMatch;

    if (isDuplicate) {
    }

    return isDuplicate;
  };
  const linkDuplicateEvents = async (crmTask, externalEvent) => {
    try {
      const taskApi = new DataApi('tasks');
      const externalEventId = externalEvent.id.replace(
        /^(google-|outlook-)/,
        ''
      );
      const source = externalEvent.source;

      await taskApi.update(crmTask.id, {
        parent_reference_id: externalEventId,
        parent_reference_object: source,
        source: source,
      });

      return true;
    } catch (error) {
      return false;
    }
  };

  const fetchCrmTasks = async () => {
    try {
      setIsFetchingTasks(true);
      const taskApi = new DataApi('tasks');
      const response = await taskApi.fetchAll();

      if (response && response.data) {
        const tasks = response.data;

        const taskEvents = tasks
          .filter(task => {
            if (!task.due_date) return false;

            // Only hide CRM task if a real matching external event exists
            const hasGoogle = showGoogleEvents && googleEvents.length > 0;
            const hasOutlook = showOutlookEvents && outlookEvents.length > 0;

            if (hasGoogle) {
              // Prefer strict link by parent_reference_id; fallback to heuristic
              const matchedGoogle = googleEvents.find(googleEvent => {
                const externalId = (googleEvent.id || '').replace(
                  /^google-/,
                  ''
                );
                const directLink =
                  task.parent_reference_object === 'google' &&
                  task.parent_reference_id &&
                  task.parent_reference_id === externalId;
                return directLink || isDuplicateEvent(task, googleEvent);
              });
              if (matchedGoogle) {
                linkDuplicateEvents(task, matchedGoogle);
                return false;
              }
            }

            if (hasOutlook) {
              const matchedOutlook = outlookEvents.find(outlookEvent => {
                const externalId = (outlookEvent.id || '').replace(
                  /^outlook-/,
                  ''
                );
                const directLink =
                  task.parent_reference_object === 'outlook' &&
                  task.parent_reference_id &&
                  task.parent_reference_id === externalId;
                return directLink || isDuplicateEvent(task, outlookEvent);
              });
              if (matchedOutlook) {
                linkDuplicateEvents(task, matchedOutlook);
                return false;
              }
            }

            return true;
          })
          .map(task => {
            const startDate = task.meeting_start_datetime
              ? new Date(task.meeting_start_datetime)
              : new Date(task.due_date);

            const endDate = task.meeting_end_datetime
              ? new Date(task.meeting_end_datetime)
              : new Date(startDate.getTime() + 3600000); // Add 1 hour

            return {
              id: `crm-task-${task.id}`,
              title: task.title || 'Untitled Task',
              start: startDate,
              end: endDate,
              resource: {
                id: task.id,
                title: task.title,
                description: task.description,
                priority: task.priority,
                status: task.status,
                type: task.type,
                ownerid: task.ownerid,
                ownername: task.ownername,
                due_date: task.due_date,
                meeting_start_datetime: task.meeting_start_datetime,
                meeting_end_datetime: task.meeting_end_datetime,
                parent_reference_id: task.parent_reference_id,
                parent_reference_object: task.parent_reference_object,
                source: task.source,
                isCrmTask: true,
              },
            };
          });

        const totalTasks = tasks.length;
        const filteredTasks = taskEvents.length;
        const duplicatesFiltered = totalTasks - filteredTasks;

        if (duplicatesFiltered > 0) {
        }

        setCrmTasks(taskEvents);
      }
    } catch (error) {
      console.error('Error fetching CRM tasks:', error);
    } finally {
      setIsFetchingTasks(false);
    }
  };

  const getTaskColor = type => {
    const colors = {
      Call: '#90CAF9',
      Task: '#A5D6A7', // 🟢 Light Green
      Email: '#FFF59D', // 🟡 Light Yellow
      Meeting: '#CE93D8', // 🟣 Light Purple
      Event: '#F48FB1', // 🌸 Light Pink
      Notes: '#FFAB91', // 🟠 Light Orange
      'Follow-up': '#80DEEA', // 🔷 Light Cyan
      Reminder: '#FFE082', // 💛 Light Amber
      WhatsApp: '#81C784', // 💚 Light Green (WhatsApp)
      Other: '#B0BEC5',
    };
    return colors[type] || '#E0E0E0'; // Default light gray
  };

  // const createCalendarEvent = async (taskData) => {
  //   try {
  //     const currentUser = getCurrentUser();
  //     if (!currentUser) {
  //       console.error('Cannot create event: No user logged in');
  //       return;
  //     }

  //     // Prepare event data for calendar
  //     const eventData = {
  //       title: taskData.title,
  //       description: taskData.description || '',
  //       startdatetime: taskData.startdatetime || taskData.targetdate,
  //       enddatetime: taskData.enddatetime || moment(taskData.startdatetime || taskData.targetdate).add(1, 'hour').toDate(),
  //       priority: taskData.priority || 'Normal',
  //       status: taskData.status || 'Not Started',
  //       type: taskData.type || 'Event',
  //       ownerid: taskData.ownerid || currentUser.id,
  //       ownername: taskData.ownername || currentUser.username,
  //       guests: taskData.guests || []
  //     };

  //     // Create Google Calendar event if Google is active
  //     if (showGoogleEvents && localStorage.getItem('googleAccessToken')) {
  //       const googleEventData = {
  //         summary: eventData.title,
  //         description: eventData.description,
  //         start: {
  //           dateTime: moment(eventData.startdatetime).format('YYYY-MM-DDTHH:mm:ss'),
  //           timeZone: 'Asia/Kolkata'
  //         },
  //         end: {
  //           dateTime: moment(eventData.enddatetime).format('YYYY-MM-DDTHH:mm:ss'),
  //           timeZone: 'Asia/Kolkata'
  //         },
  //         attendees: eventData.guests.map(email => ({ email }))
  //       };

  //       const response = await googleCalendarApi.createEvent(googleEventData);
  //       if (response.success) {
  //         alert('✅ Event created in Google Calendar!');
  //         handleSyncGoogle();
  //       } else {
  //         alert('❌ Failed to create event in Google Calendar');
  //       }
  //     } else {
  //       // Create Outlook event
  //       const outlookEventData = {
  //         subject: eventData.title,
  //         body: {
  //           contentType: "HTML",
  //           content: eventData.description || "Event created from Spark CRM"
  //         },
  //         start: {
  //           dateTime: moment(eventData.startdatetime).format('YYYY-MM-DDTHH:mm:ss'),
  //           timeZone: "India Standard Time"
  //         },
  //         end: {
  //           dateTime: moment(eventData.enddatetime).format('YYYY-MM-DDTHH:mm:ss'),
  //           timeZone: "India Standard Time"
  //         },
  //         attendees: eventData.guests.map(email => ({
  //           emailAddress: { address: email },
  //           type: "required"
  //         }))
  //       };

  //       const response = await outlookApi.createEvent(outlookEventData);
  //       if (response.success) {
  //         alert('✅ Event created in Outlook!');
  //         fetchOutlookEvents();
  //       } else {
  //         alert('❌ Failed to create event in Outlook');
  //       }
  //     }
  //   } catch (error) {
  //     console.error('❌ Error creating calendar event:', error);
  //     alert('❌ Error creating calendar event: ' + error.message);
  //   }
  // };

  useEffect(() => {
    setEvents([]);
    setGoogleEvents([]);
    setOutlookEvents([]);

    // Initialize calendar with automatic sync
    const initializeCalendar = async () => {
      // Fetch CRM tasks first (always show)
      fetchCrmTasks();

      // Check user's mail configuration and auto-sync
      const configResult = await checkAndSyncMailConfig();

      if (configResult.hasConfig) {
        console.log(`📧 Auto-syncing with ${configResult.type}`);

        // Fetch additional info for the configured service
        if (configResult.type === 'outlook') {
          fetchOutlookUserInfo();
          fetchAvailableCalendars();
          fetchOutlookAccounts();
        }
      } else {
        console.log('📋 Showing CRM tasks only - no mail config');
      }

      setIsLoading(false);
    };

    initializeCalendar();
  }, []);

  // Re-fetch CRM tasks when Google/Outlook events change to apply deduplication
  useEffect(() => {
    if (googleEvents.length > 0 || outlookEvents.length > 0) {
      console.log(
        '🔄 External events loaded, re-fetching CRM tasks for deduplication...'
      );
      fetchCrmTasks();
    }
  }, [
    googleEvents.length,
    outlookEvents.length,
    showGoogleEvents,
    showOutlookEvents,
  ]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (showCreateDropdown && !event.target.closest('[data-dropdown]')) {
        setShowCreateDropdown(false);
      }
      if (showSearchResults && !event.target.closest('[data-search]')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateDropdown, showSearchResults]);

  const fetchOutlookEvents = async () => {
    console.log('fetching outlook events');
    try {
      setIsLoading(true);
      setOutlookSyncStatus('Syncing with Outlook...');

      const startOfYear = currentDate
        .clone()
        .startOf('year')
        .format('YYYY-MM-DD');
      const endOfYear = currentDate.clone().endOf('year').format('YYYY-MM-DD');

      const events = await outlookApi.getCalendarEvents(
        null,
        startOfYear,
        endOfYear
      );
      console.log('outlook events', events);

      const formattedOutlookEvents = events.map(event => {
        let startDate, endDate;

        console.log('🔍 DEBUG Outlook Event:', event.subject);
        console.log(
          '   Start raw:',
          event.start?.dateTime,
          'timezone:',
          event.start?.timeZone
        );
        console.log(
          '   End raw:',
          event.end?.dateTime,
          'timezone:',
          event.end?.timeZone
        );

        if (event.start?.dateTime) {
          const rawStart = event.start.dateTime;

          if (rawStart.includes('Z') || rawStart.match(/[+-]\d{2}:\d{2}$/)) {
            console.log('     Detected UTC/TZ format, converting to IST');
            startDate = moment.tz(rawStart, 'Asia/Kolkata').toDate();
          } else {
            console.log('    No timezone, treating as IST');
            const dateStr = rawStart.split('.')[0];
            startDate = moment.tz(dateStr, 'Asia/Kolkata').toDate();
          }
        } else if (event.start?.date) {
          startDate = moment.tz(event.start.date, 'Asia/Kolkata').toDate();
        } else {
          startDate = moment.tz('Asia/Kolkata').toDate();
        }

        if (event.end?.dateTime) {
          const rawEnd = event.end.dateTime;

          if (rawEnd.includes('Z') || rawEnd.match(/[+-]\d{2}:\d{2}$/)) {
            endDate = moment.tz(rawEnd, 'Asia/Kolkata').toDate();
          } else {
            const dateStr = rawEnd.split('.')[0];
            endDate = moment.tz(dateStr, 'Asia/Kolkata').toDate();
          }
        } else if (event.end?.date) {
          endDate = moment.tz(event.end.date, 'Asia/Kolkata').toDate();
        } else {
          endDate = moment.tz('Asia/Kolkata').add(1, 'hour').toDate();
        }

        console.log(
          '    Parsed start:',
          moment(startDate).format('YYYY-MM-DD HH:mm')
        );
        console.log(
          '    Parsed end:',
          moment(endDate).format('YYYY-MM-DD HH:mm')
        );

        const guests =
          event.attendees?.map(
            attendee => attendee.emailAddress?.address || attendee.emailAddress
          ) || [];

        let status = 'Scheduled';
        if (event.responseStatus) {
          switch (event.responseStatus.response) {
            case 'declined':
              status = 'Declined';
              break;
            case 'accepted':
              status = 'Accepted';
              break;
            case 'tentative':
              status = 'Tentative';
              break;
            default:
              status = 'Scheduled';
          }
        }

        return {
          id: `outlook-${event.id}`,
          title: event.subject || 'Untitled Event',
          start: startDate,
          end: endDate,
          resource: {
            id: `outlook-${event.id}`,
            title: event.subject || 'Untitled Event',
            description: event.bodyPreview || event.body?.content || '',
            priority: 'Normal',
            status: status,
            type: 'Event',
            location: event.location?.displayName || '',
            guests: guests,
            targetdate: startDate,
            startdatetime: startDate,
            enddatetime: endDate,
            ownerid: event.organizer?.emailAddress?.address || '',
            ownername: event.organizer?.emailAddress?.name || '',
            outlookEventId: event.id,

            outlookEvent: event,
          },
          allDay: event.isAllDay || false,
          source: 'outlook',
        };
      });

      setOutlookEvents(
        Array.isArray(formattedOutlookEvents) ? formattedOutlookEvents : []
      );
      setOutlookSyncStatus(
        `✓ ${formattedOutlookEvents.length} Outlook Calendar events loaded`
      );

      localStorage.setItem('selectedCalendarType', 'outlook');

      setTimeout(() => {
        setCurrentDate(currentDate.clone());
        setOutlookSyncStatus('');
      }, 3000);
    } catch (error) {
      let errorMessage = '⚠ Outlook sync failed';

      // Only show "config missing" error if backend explicitly says noConfig
      if (error.response?.data?.noConfig === true) {
        errorMessage = '⚠️ Outlook mail configuration is missing';
        setOutlookEvents([]);
        setEvents([]);
      } else if (
        error.message?.includes('504') ||
        error.message?.includes('Gateway Timeout')
      ) {
        errorMessage = '⚠ Outlook is slow, retrying...';
        setTimeout(() => {
          fetchOutlookEvents();
        }, 3000);
      } else if (error.message?.includes('Network')) {
        errorMessage = '⚠ Network error, please check connection';
      } else {
        // For other errors, show a generic message and log details
        console.error(
          '📋 Outlook sync error:',
          error.response?.data || error.message
        );
        errorMessage = '⚠ Outlook sync failed - please try again';
      }

      setOutlookSyncStatus(errorMessage);
      setTimeout(() => setOutlookSyncStatus(''), 8000);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOutlookUserInfo = async () => {
    try {
      const response = await outlookApi.getUserInfo();

      if (response.success && response.user) {
        setOutlookUserInfo(response.user);
      }
    } catch (error) { }
  };

  const fetchAvailableCalendars = async () => {
    try {
      const calendars = await outlookApi.getAllCalendars();

      const defaultCalendars = [
        {
          id: 'primary',
          name: 'Muskan Khan',
          color: '#1a73e8',
          isDefault: true,
        },
      ];

      const allCalendars = [...defaultCalendars, ...calendars];
      setAvailableCalendars(allCalendars);
    } catch (error) {
      setAvailableCalendars([
        {
          id: 'primary',
          name: 'Muskan Khan',
          color: '#1a73e8',
          isDefault: true,
        },
      ]);
    }
  };

  const fetchOutlookAccounts = async () => {
    try {
      const response = await outlookApi.getOutlookAccounts();
      if (response.success && response.accounts) {
        setOutlookAccounts(response.accounts);
        if (response.accounts.length > 0) {
          const savedAccountId = localStorage.getItem(
            'selectedOutlookAccountId'
          );
          const defaultAccount = savedAccountId
            ? response.accounts.find(acc => acc.id === parseInt(savedAccountId))
            : response.accounts[0];
          setSelectedOutlookAccount(defaultAccount || response.accounts[0]);
        }
      }
    } catch (error) {
      setOutlookAccounts([]);
    }
  };

  const handleSyncOutlook = async () => {
    setIsSyncingOutlook(true);
    setIsLoading(true);

    setEvents([]);
    setGoogleEvents([]);

    setShowGoogleEvents(false);
    setShowOutlookEvents(true);

    setTimeout(() => {
      setEvents([]);
    }, 50);

    await fetchOutlookEvents();
    setIsSyncingOutlook(false);
    setIsLoading(false);
  };

  const handleGoogleLogout = () => {
    localStorage.removeItem('googleAccessToken');
    localStorage.removeItem('googleRefreshToken');
    localStorage.removeItem('googleUserInfo');
    localStorage.removeItem('selectedCalendarType');
    setGoogleEvents([]);
    setGoogleSyncStatus('');
    setGoogleAccessToken(null);

    setShowGoogleEvents(false);
    setShowOutlookEvents(true);

    setEvents([]);

    setTimeout(() => {
      fetchOutlookEvents();
    }, 100);

    alert(
      'Google Calendar logged out successfully! You can now login with a different account.'
    );
  };

  const handleGoogleAuth = async () => {
    try {
      console.log('🚀 handleGoogleAuth called - Opening popup...');
      setGoogleSyncStatus('Opening Google authentication...');

      const authResponse = await googleCalendarApi.getAuthUrl();
      const authUrl = authResponse.authUrl;
      console.log('🔗 Auth URL received:', authUrl.substring(0, 50) + '...');

      const popup = window.open(
        authUrl,
        'googleAuth',
        'width=500,height=600,scrollbars=yes,resizable=yes,left=' +
        (window.screen.width / 2 - 250) +
        ',top=' +
        (window.screen.height / 2 - 300)
      );

      // Check if popup was blocked
      if (!popup) {
        console.error('❌ Popup blocked by browser!');
        setGoogleSyncStatus(
          '⚠ Popup blocked! Please allow popups for this site.'
        );
        setTimeout(() => setGoogleSyncStatus(''), 5000);
        return;
      }

      console.log('✅ Popup opened successfully');

      const checkClosed = setInterval(() => {
        if (popup && popup.closed) {
          console.log('🪟 Popup closed - checking for localStorage tokens...');
          clearInterval(checkClosed);

          // Check if tokens were stored in localStorage (fallback method)
          const authComplete = localStorage.getItem('googleAuthComplete');
          const authTokens = localStorage.getItem('googleAuthTokens');

          if (authComplete === 'true' && authTokens) {
            console.log('✅ Found tokens in localStorage (fallback method)!');
            try {
              const tokens = JSON.parse(authTokens);

              // Save tokens to localStorage
              localStorage.setItem('googleAccessToken', tokens.access_token);
              console.log('💾 Access token saved to localStorage');

              if (tokens.refresh_token) {
                localStorage.setItem(
                  'googleRefreshToken',
                  tokens.refresh_token
                );
                console.log('💾 Refresh token saved to localStorage');
              }

              // Clear fallback flags
              localStorage.removeItem('googleAuthComplete');
              localStorage.removeItem('googleAuthTokens');

              setTimeout(() => {
                console.log(
                  '🔄 Calling handleSyncGoogle (from localStorage fallback)...'
                );
                handleSyncGoogle();
              }, 1000);
            } catch (error) {
              console.error(
                '❌ Error parsing tokens from localStorage:',
                error
              );
            }
          }

          setGoogleSyncStatus('');
        }
      }, 1000);

      const messageListener = async event => {
        console.log('📨 Message received from popup:', event.data);
        console.log('📍 Message origin:', event.origin);
        console.log('📍 Current origin:', window.location.origin);
        // TEMPORARILY: Accept messages from any origin for debugging
        // TODO: Re-enable origin check after debugging
        // if (event.origin !== window.location.origin) {
        //   console.warn('⚠️ Message origin mismatch:', event.origin, 'vs', window.location.origin);
        //   return;
        // }

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          console.log('✅ Google Auth Success message received!');
          const { tokens } = event.data;
          console.log('🔑 Tokens received:', {
            hasAccessToken: !!tokens.access_token,
            hasRefreshToken: !!tokens.refresh_token,
          });

          // Save tokens to localStorage
          localStorage.setItem('googleAccessToken', tokens.access_token);
          console.log('💾 Access token saved to localStorage');
          if (tokens.refresh_token) {
            localStorage.setItem('googleRefreshToken', tokens.refresh_token);
            console.log('💾 Refresh token saved to localStorage');
          }

          if (popup && !popup.closed) {
            popup.close();
          }
          window.removeEventListener('message', messageListener);
          clearInterval(checkClosed);

          console.log('🔄 Will call handleSyncGoogle in 1 second...');
          setTimeout(() => {
            console.log('🔄 Calling handleSyncGoogle now...');
            handleSyncGoogle();
          }, 1000);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          setGoogleSyncStatus('⚠ Authentication failed: ' + event.data.error);
          setTimeout(() => setGoogleSyncStatus(''), 5000);
          if (popup && !popup.closed) {
            popup.close();
          }
          window.removeEventListener('message', messageListener);
          clearInterval(checkClosed);
        }
      };

      console.log(
        '👂 Message listener registered - waiting for popup response...'
      );
      window.addEventListener('message', messageListener);
    } catch (error) {
      console.error('❌ Error starting Google authentication:', error);
      setGoogleSyncStatus('⚠ Failed to start Google authentication');
      setTimeout(() => setGoogleSyncStatus(''), 5000);
    }
  };

  const handleSyncGoogle = async () => {
    setIsSyncingGoogle(true);
    setIsLoading(true);
    setGoogleSyncStatus('Syncing with Google...');

    setEvents([]);
    setOutlookEvents([]);

    setShowOutlookEvents(false);
    setShowGoogleEvents(true);

    setTimeout(() => {
      setEvents([]);
    }, 50);
    try {
      // Try to load events from backend (using DB refresh token)
      const startOfYear = currentDate
        .clone()
        .startOf('year')
        .format('YYYY-MM-DD');
      const endOfYear = currentDate.clone().endOf('year').format('YYYY-MM-DD');

      const events = await googleCalendarApi.getCalendarEvents(
        'primary',
        startOfYear,
        endOfYear
      );

      const formattedGoogleEvents = events.map(event => {
        let startDate, endDate;

        if (event.start?.dateTime) {
          startDate = moment.tz(event.start.dateTime, 'Asia/Kolkata').toDate();
        } else if (event.start?.date) {
          startDate = moment.tz(event.start.date, 'Asia/Kolkata').toDate();
        } else {
          startDate = moment.tz('Asia/Kolkata').toDate();
        }

        if (event.end?.dateTime) {
          endDate = moment.tz(event.end.dateTime, 'Asia/Kolkata').toDate();
        } else if (event.end?.date) {
          endDate = moment.tz(event.end.date, 'Asia/Kolkata').toDate();
        } else {
          endDate = moment.tz('Asia/Kolkata').add(1, 'hour').toDate();
        }

        console.log(
          '📅 Google event loaded:',
          event.summary,
          '→ Start:',
          moment(startDate).format('HH:mm'),
          'End:',
          moment(endDate).format('HH:mm')
        );

        const guests = event.attendees?.map(attendee => attendee.email) || [];

        return {
          id: `google-${event.id}`,
          title: event.summary || 'Untitled Event',
          start: startDate,
          end: endDate,
          resource: {
            id: `google-${event.id}`,
            title: event.summary || 'Untitled Event',
            description: event.description || '',
            priority: 'Normal',
            status: 'Scheduled',
            type: 'Event',
            location: event.location || '',
            guests: guests,
            targetdate: startDate,
            startdatetime: startDate,
            enddatetime: endDate,
            googleEventId: event.id,
            googleEvent: event,
          },
          allDay: event.start?.date ? true : false,
          source: 'google',
        };
      });

      setGoogleEvents(formattedGoogleEvents);
      setGoogleSyncStatus(
        `✓ ${formattedGoogleEvents.length} Google Calendar events loaded`
      );

      localStorage.setItem('selectedCalendarType', 'google');

      try {
        const userInfo = await googleCalendarApi.getUserInfo();
        localStorage.setItem('googleUserInfo', JSON.stringify(userInfo));
      } catch (userError) { }

      // ✅ RESET LOADER - Events loaded successfully!
      setIsSyncingGoogle(false);
      setIsLoading(false);

      setTimeout(() => {
        setGoogleSyncStatus('');
      }, 3000);
    } catch (error) {
      let errorMessage = '⚠ Google Calendar sync failed';
      let shouldOpenPopup = false;

      // Only show "config missing" error if backend explicitly says noConfig
      if (error.response?.data?.noConfig === true) {
        errorMessage = '⚠️ Google Calendar mail configuration is missing';
        setGoogleEvents([]);
        setEvents([]);
      } else if (
        error.response?.data?.error === 'Missing refresh token in database' ||
        error.response?.status === 401 ||
        error.message?.includes('401') ||
        error.message?.includes('Unauthorized') ||
        error.message?.includes('invalid_grant')
      ) {
        errorMessage = '🔐 Opening Google login...';
        shouldOpenPopup = true;
      } else if (error.message?.includes('Network')) {
        errorMessage = '⚠ Network error, please check connection';
      } else {
        // For other errors, show a generic message and log details
        console.error(
          '📋 Google Calendar sync error:',
          error.response?.data || error.message
        );
        errorMessage = '⚠ Google Calendar sync failed - please try again';
      }

      setGoogleSyncStatus(errorMessage);

      if (shouldOpenPopup) {
        setIsSyncingGoogle(false);
        setIsLoading(false);
        setTimeout(() => {
          handleGoogleAuth();
        }, 500);
      } else {
        // Show error for 5 seconds instead of 10
        setTimeout(() => setGoogleSyncStatus(''), 5000);
        setIsSyncingGoogle(false);
        setIsLoading(false);
      }
    }
  };

  const handleSearchPeople = async query => {
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      let results = [];

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isEmail = emailRegex.test(query);

      if (isEmail) {
        const emailParts = query.split('@');
        const name = emailParts[0]
          .replace(/[._]/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
        const domain = emailParts[1];

        const avatar = name
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);

        const isCompanyEmail =
          domain.includes('ibirdsservices.com') ||
          domain.includes('company.com') ||
          domain.includes('organization.com');

        const photoId = Math.floor(Math.random() * 1000);
        const photo = `https://images.unsplash.com/photo-${1500000000000 + photoId}?w=150&h=150&fit=crop&crop=face`;

        results = [
          {
            id: `dynamic-${Date.now()}`,
            name: name,
            email: query,
            avatar: avatar,
            photo: photo,
            department: isCompanyEmail ? 'Company' : 'External',
            position: isCompanyEmail ? 'Team Member' : 'Contact',
            isDynamic: true,
          },
        ];
      } else {
        const existingContacts = [
          {
            id: '1',
            name: 'Muskan Khan',
            email: 'muskan.k@ibirdsservices.com',
            avatar: 'MK',
            photo:
              'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
            department: 'Management',
            position: 'CEO & Founder',
          },
          {
            id: '2',
            name: 'Amit Sharma',
            email: 'amit.sharma@ibirdsservices.com',
            avatar: 'AS',
            photo:
              'https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=150&h=150&fit=crop&crop=face',
            department: 'Development',
            position: 'Lead Developer',
          },
        ];

        results = existingContacts.filter(
          person =>
            person.name.toLowerCase().includes(query.toLowerCase()) ||
            person.email.toLowerCase().includes(query.toLowerCase())
        );
      }

      setSearchResults(results);
      setShowSearchResults(true);
    } catch (error) { }
  };

  const handleSelectPerson = person => {
    const now = moment.tz('Asia/Kolkata');

    const newTaskData = {
      id: null,
      title: `Meeting with ${person.name}`,
      description: '',
      start: now.format('YYYY-MM-DD HH:mm'),
      end: now.clone().add(1, 'hour').format('YYYY-MM-DD HH:mm'),
      priority: 'Normal',
      status: 'Not Started',
      type: 'Event',
      location: '',
      guests: [person.email],
      targetdate: now.toDate(),
      startdatetime: now.toDate(),
      enddatetime: now.clone().add(1, 'hour').toDate(),
    };

    setModalPosition({
      x: window.innerWidth / 2 - 225,
      y: window.innerHeight / 2 - 300,
    });

    setTaskData(newTaskData);
    setShowTaskModal(true);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const handleSelectSlot = ({ start, end }, e) => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      alert('❌ Please login first');
      return;
    }

    // Store the clicked date in EditLayout's expected format
    const clickedDate = moment(start);
    const startDateTime = clickedDate.format('YYYY-MM-DD HH:mm:ss');
    const endDateTime = clickedDate
      .clone()
      .add(1, 'hour')
      .format('YYYY-MM-DD HH:mm:ss');

    setSelectedSlotDate({
      defaultDueDate: clickedDate.format('YYYY-MM-DD'),
      defaultMeetingStartDateTime: startDateTime,
      defaultMeetingEndDateTime: endDateTime,
    });

    // Open EditLayoutModal for creating new task
    setEditingRecordId(null); // null = new record
    setShowEditLayoutModal(true);
  };

  const handleSelectEvent = async (event, e) => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        alert('❌ Please login first');
        return;
      }

      console.log('📌 Event clicked:', event);
      console.log('   Event ID:', event.id);
      console.log('   Event source:', event.source);
      console.log('   Resource ID:', event.resource?.id);
      console.log('   Is CRM Task:', event.resource?.isCrmTask);
      console.log('   Full event object:', JSON.stringify(event, null, 2));

      // Check if this is a CRM task or external event
      let taskId = null;

      if (event.resource?.isCrmTask) {
        // ✅ This is a CRM task - use its ID directly
        taskId = event.resource.id;
        console.log('✅ CRM Task ID:', taskId);
      } else if (event.source === 'google' || event.source === 'outlook') {
        // ❌ This is an external event - need to find linked task in DB
        console.log('🔍 External event - searching for linked task...');

        const taskApi = new DataApi('tasks');
        console.log('🔍 Fetching all tasks from DB...');

        let allTasks;
        try {
          allTasks = await taskApi.fetchAll();
          console.log(
            '✅ Tasks fetched successfully:',
            allTasks?.data?.length || 0,
            'tasks'
          );
        } catch (fetchError) {
          console.error('❌ Error fetching tasks from DB:', fetchError);
          alert(
            '❌ Network error: Cannot connect to database. Please check backend server.'
          );
          return;
        }

        // Extract the actual event ID (remove "google-" or "outlook-" prefix)
        const externalEventId = event.id.replace(/^(google-|outlook-)/, '');
        console.log('   External Event ID:', externalEventId);

        // Find task with matching parent_reference_id
        const linkedTask = allTasks?.data?.find(
          task => task.parent_reference_id === externalEventId
        );

        if (linkedTask) {
          taskId = linkedTask.id;
          console.log('✅ Found linked task ID:', taskId);
        } else {
          // ❌ No linked task found - CREATE one automatically!
          console.log(
            '❌ No linked task found - creating CRM task from external event...'
          );

          const startMoment = event.start ? moment(event.start) : moment();
          const endMoment = event.end
            ? moment(event.end)
            : startMoment.clone().add(1, 'hour');

          const newTaskData = {
            title: event.title || 'Untitled Event',
            description: event.resource?.description || '',
            type: 'Meeting',
            priority: 'Low',
            status: 'Not Started',
            due_date: startMoment.format('YYYY-MM-DD'),
            meeting_start_datetime: startMoment.format('YYYY-MM-DD HH:mm:ss'),
            meeting_end_datetime: endMoment.format('YYYY-MM-DD HH:mm:ss'),
            ownerid: currentUser.id,
            source: event.source,
            parent_reference_id: externalEventId,
            parent_reference_object: event.source,
          };

          console.log('📝 Creating CRM task:', newTaskData);
          console.log(
            '   parent_reference_id length:',
            externalEventId?.length || 0
          );

          let createResponse;
          try {
            createResponse = await taskApi.create(newTaskData);
            console.log('📥 Create response:', createResponse);
          } catch (createApiError) {
            console.error('❌ Error creating task:', createApiError);
            console.error('   Error message:', createApiError.message);
            console.error('   Error response:', createApiError.response?.data);

            // Check if it's a VARCHAR length error
            if (
              createApiError.message?.includes('too long') ||
              createApiError.message?.includes('22001')
            ) {
              alert(
                '❌ Database Error: Event ID is too long!\n\n⚠️ SOLUTION:\n1. Run DATABASE_FIX_VARCHAR.sql\n2. Or contact your DBA\n\nEvent ID: ' +
                externalEventId?.substring(0, 50) +
                '...'
              );
            } else {
              alert(
                '❌ Error creating task: ' +
                (createApiError.message || 'Unknown error')
              );
            }
            return;
          }

          // Handle different response structures
          const createdTaskId =
            createResponse?.data?.id ||
            createResponse?.id ||
            createResponse?.data?.data?.id;

          if (createdTaskId) {
            taskId = createdTaskId;
            console.log('✅ Created CRM task ID:', taskId);

            // Refresh tasks to show the new link
            setTimeout(() => fetchCrmTasks(), 500);
          } else {
            console.error('❌ Failed to create CRM task - no ID returned');
            console.error(
              'Full response:',
              JSON.stringify(createResponse, null, 2)
            );
            alert('❌ Error creating task link');
            return;
          }
        }
      } else {
        console.log(
          '❌ Unknown event type - probably a CRM task without isCrmTask flag'
        );
        // Try to use the ID anyway if it looks like a UUID
        const possibleTaskId = event.resource?.id || event.id;
        if (
          possibleTaskId &&
          !possibleTaskId.includes('google-') &&
          !possibleTaskId.includes('outlook-')
        ) {
          taskId = possibleTaskId;
          console.log('🔄 Attempting to use ID as task UUID:', taskId);
        } else {
          console.error('❌ Cannot determine task ID');
          return;
        }
      }

      // Open EditLayoutModal for editing existing task
      if (taskId) {
        console.log('🎯 Opening modal with task ID:', taskId);
        setSelectedSlotDate(null); // Clear date for editing
        setEditingRecordId(taskId); // task ID for editing
        setShowEditLayoutModal(true);
      } else {
        console.error('❌ No valid task ID found');
        alert('❌ Cannot open event - no valid task ID');
      }
    } catch (mainError) {
      console.error('❌ MAIN ERROR in handleSelectEvent:', mainError);
      console.error('Error message:', mainError.message);
      console.error('Error stack:', mainError.stack);
      alert('❌ Error opening event: ' + mainError.message);
    }
  };

  const handleTaskSubmit = async savedTaskData => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        alert('❌ User not logged in');
        return;
      }

      console.log('🔍 DEBUG - Event Creation:');
      console.log('   showGoogleEvents:', showGoogleEvents);
      console.log('   showOutlookEvents:', showOutlookEvents);
      console.log(
        '   googleAccessToken:',
        !!localStorage.getItem('googleAccessToken')
      );
      console.log('   hasMailConfig:', hasMailConfig);

      let createdEventId = null;
      let eventSource = null;

      // ✅ SCENARIO 1: User has Google Account
      const hasGoogleToken =
        localStorage.getItem('googleAccessToken') || googleAccessToken;
      if (showGoogleEvents && hasGoogleToken) {
        console.log('✅ Creating event in Google Calendar...');
        try {
          const startMoment = moment(savedTaskData.startdatetime).tz(
            'Asia/Kolkata'
          );
          const endMoment = moment(savedTaskData.enddatetime).tz(
            'Asia/Kolkata'
          );

          const guestsList = savedTaskData.guests || [];
          const attendeesList = guestsList.map(guest => {
            const email =
              typeof guest === 'string'
                ? guest
                : guest.email || guest.emailAddress?.address;
            return {
              email: email,
              responseStatus: 'needsAction',
            };
          });

          const googleEventData = {
            summary: savedTaskData.title || 'New Event',
            description: savedTaskData.description || '',
            location: savedTaskData.location || '',
            start: {
              dateTime: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
              timeZone: 'Asia/Kolkata',
            },
            end: {
              dateTime: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
              timeZone: 'Asia/Kolkata',
            },
            attendees: attendeesList,
          };

          const response = await googleCalendarApi.createEvent(googleEventData);

          if (response.success && response.data?.id) {
            createdEventId = response.data.id;
            eventSource = 'google';
            console.log('✅ Google event created! ID:', createdEventId);
            alert('✅ Event created in Google Calendar!');
          } else {
            console.log('❌ Google event creation failed:', response);
          }
        } catch (error) {
          console.error('❌ Error creating Google event:', error);
          const errorMsg = error.response?.data?.message || error.message || '';
          if (
            errorMsg.includes('invalid authentication') ||
            errorMsg.includes('Expected OAuth 2') ||
            errorMsg.includes('401')
          ) {
            localStorage.removeItem('googleAccessToken');
            localStorage.removeItem('googleRefreshToken');
            localStorage.removeItem('googleUserInfo');
            alert('🔑 Google Authentication Expired!\n\n✅ Please login again');
            setGoogleEvents([]);
            setGoogleSyncStatus('⚠️ Authentication expired');
          } else {
            alert('❌ Error creating Google event: ' + errorMsg);
          }
        }
      }
      // ✅ SCENARIO 2: User has Outlook Account
      else if (showOutlookEvents) {
        console.log('✅ Creating event in Outlook Calendar...');
        try {
          const startMoment = moment(savedTaskData.startdatetime).tz(
            'Asia/Kolkata'
          );
          const endMoment = moment(savedTaskData.enddatetime).tz(
            'Asia/Kolkata'
          );

          const attendeesList =
            savedTaskData.guests?.map(guest => ({
              emailAddress: {
                address: guest,
                name: guest,
              },
              type: 'required',
            })) || [];

          const outlookEventData = {
            subject: savedTaskData.title || 'New Event',
            body: {
              contentType: 'HTML',
              content: savedTaskData.description || '',
            },
            start: {
              dateTime: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
              timeZone: 'Asia/Kolkata',
            },
            end: {
              dateTime: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
              timeZone: 'Asia/Kolkata',
            },
            location: {
              displayName: savedTaskData.location || '',
            },
            attendees: attendeesList,
          };

          const response = await outlookApi.createEvent(outlookEventData);

          if (response.success && response.event?.id) {
            createdEventId = response.event.id;
            eventSource = 'outlook';
            console.log('✅ Outlook event created! ID:', createdEventId);
            alert('✅ Event created in Outlook Calendar!');
          } else {
            console.log('❌ Outlook event creation failed:', response);
          }
        } catch (error) {
          console.error('❌ Error creating Outlook event:', error);
          alert('❌ Error creating Outlook event: ' + (error.message || ''));
        }
      } else {
        console.log('ℹ️ No external calendar account - saving to DB only');
      }

      // ✅ ALWAYS save to Task DB (regardless of account)
      try {
        const taskApi = new DataApi('tasks');

        const taskData = {
          title: savedTaskData.title || 'Untitled Task',
          description: savedTaskData.description || '',
          type: savedTaskData.type || 'Meeting',
          priority: savedTaskData.priority || 'Low',
          status: savedTaskData.status || 'Not Started',
          due_date: savedTaskData.startdatetime
            ? moment(savedTaskData.startdatetime).format('YYYY-MM-DD')
            : null,
          meeting_start_datetime: savedTaskData.startdatetime
            ? moment(savedTaskData.startdatetime).format('YYYY-MM-DD HH:mm:ss')
            : null,
          meeting_end_datetime: savedTaskData.enddatetime
            ? moment(savedTaskData.enddatetime).format('YYYY-MM-DD HH:mm:ss')
            : null,
          ownerid: currentUser.id,
          source: eventSource || 'manual',
          parent_reference_id: createdEventId,
          parent_reference_object: eventSource,
        };

        await taskApi.create(taskData);
        console.log('✅ Task saved to database:', {
          source: eventSource || 'manual',
          eventId: createdEventId,
          title: taskData.title,
        });

        // Refresh views
        if (eventSource === 'google') {
          setTimeout(() => handleSyncGoogle(), 1000);
        } else if (eventSource === 'outlook') {
          setTimeout(() => fetchOutlookEvents(), 1000);
        }

        // Refresh CRM tasks
        setTimeout(() => fetchCrmTasks(), 1000);
      } catch (dbError) {
        console.error('❌ Error saving task to DB:', dbError);
        alert('⚠️ Event created but failed to save task in database');
      }
    } catch (error) {
      console.error('Error in handleTaskSubmit:', error);
      alert('❌ Error creating event/task');
    }

    setShowTaskModal(false);
  };

  const handleTaskUpdate = async savedTaskData => {
    if (
      showGoogleEvents &&
      localStorage.getItem('googleAccessToken') &&
      savedTaskData.googleEventId
    ) {
      try {
        const startMoment = moment(savedTaskData.startdatetime).tz(
          'Asia/Kolkata'
        );
        const endMoment = moment(savedTaskData.enddatetime).tz('Asia/Kolkata');

        const attendeesList =
          savedTaskData.guests?.map(guest => ({
            email: guest,
            responseStatus: 'needsAction',
          })) || [];

        const googleEventData = {
          summary: savedTaskData.title || 'Updated Event',
          description: savedTaskData.description || '',
          location: savedTaskData.location || '',
          start: {
            dateTime: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
            timeZone: 'Asia/Kolkata',
          },
          end: {
            dateTime: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
            timeZone: 'Asia/Kolkata',
          },
          attendees: attendeesList,
        };

        console.log(
          'Updating Google Calendar event with data:',
          googleEventData
        );
        console.log('Attendees who will receive update emails:', attendeesList);

        const response = await googleCalendarApi.updateEvent(
          savedTaskData.googleEventId,
          googleEventData
        );

        if (response.success) {
          alert('Event updated successfully!');

          setTimeout(() => {
            handleSyncGoogle();
          }, 1000);
        } else {
          alert(' Failed to update event in Google Calendar');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.message || '';
        if (
          errorMsg.includes('invalid authentication') ||
          errorMsg.includes('Expected OAuth 2') ||
          errorMsg.includes('401')
        ) {
          localStorage.removeItem('googleAccessToken');
          localStorage.removeItem('googleRefreshToken');
          localStorage.removeItem('googleUserInfo');
          alert(
            '🔑 Google Authentication Expired!\n\nYour session has expired.\n\n✅ Solution:\n1. Click OK\n2. Click "🔄 Google" button again\n3. Login with Google again'
          );
          setGoogleEvents([]);
          setGoogleSyncStatus(
            '⚠️ Authentication expired. Please click Google button to login again.'
          );
        } else {
          alert(' Error updating event: ' + errorMsg);
        }
      }
    } else {
      setTimeout(() => {
        fetchOutlookEvents();
      }, 1000);
    }
    setShowTaskModal(false);
  };

  const handleTaskDelete = async eventData => {
    if (
      showGoogleEvents &&
      localStorage.getItem('googleAccessToken') &&
      eventData.googleEventId
    ) {
      try {
        const confirmDelete = window.confirm(
          'Are you sure you want to delete this event from Google Calendar?'
        );
        if (!confirmDelete) return;

        const response = await googleCalendarApi.deleteEvent(
          eventData.googleEventId
        );

        if (response.success) {
          alert(' Event deleted successfully from Google Calendar!');

          setTimeout(() => {
            handleSyncGoogle();
          }, 1000);
        } else {
          alert('Failed to delete event from Google Calendar');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.message || '';
        if (
          errorMsg.includes('invalid authentication') ||
          errorMsg.includes('Expected OAuth 2') ||
          errorMsg.includes('401')
        ) {
          localStorage.removeItem('googleAccessToken');
          localStorage.removeItem('googleRefreshToken');
          localStorage.removeItem('googleUserInfo');
          alert(
            '🔑 Google Authentication Expired!\n\nYour session has expired.\n\n✅ Solution:\n1. Click OK\n2. Click "🔄 Google" button again\n3. Login with Google again'
          );
          setGoogleEvents([]);
          setGoogleSyncStatus(
            '⚠️ Authentication expired. Please click Google button to login again.'
          );
        } else {
          alert(' Error deleting event: ' + errorMsg);
        }
      }
    } else {
      setTimeout(() => {
        fetchOutlookEvents();
      }, 2000);
    }

    setShowTaskModal(false);
  };

  const eventStyleGetter = event => {
    let backgroundColor = '#1a73e8';
    let borderColor = '#1a73e8';

    // CRM Task - Color by Type
    if (event.resource?.isCrmTask) {
      backgroundColor = getTaskColor(event.resource.type);
      borderColor = backgroundColor;
    }
    // Outlook Event
    else if (event.source === 'outlook') {
      backgroundColor = '#0078d4';
      borderColor = '#0078d4';
    }
    // Google Calendar Event
    else if (event.source === 'google') {
      backgroundColor = '#34a853';
      borderColor = '#34a853';
    }
    // Priority-based colors (fallback)
    else if (event.resource?.priority === 'High') {
      backgroundColor = '#d32f2f';
      borderColor = '#d32f2f';
    } else if (event.resource?.priority === 'Medium') {
      backgroundColor = '#f57c00';
      borderColor = '#f57c00';
    } else if (event.resource?.priority === 'Low') {
      backgroundColor = '#388e3c';
      borderColor = '#388e3c';
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        color: '#000000', // Black text color
        borderRadius: '4px',
        border: event.resource?.isCrmTask
          ? '2px solid rgba(0,0,0,0.2)'
          : event.source === 'outlook'
            ? '2px solid #005a9e'
            : event.source === 'google'
              ? '2px solid #1e8e3e'
              : 'none',
        fontSize: '12px',
        padding: '2px 4px',
        fontWeight: '500',
      },
    };
  };

  const handleNavigate = date => {
    const currentMoment = currentDate;
    const newMoment = moment(date);
    const isGoingBack = newMoment.isBefore(currentMoment);
    const isGoingForward = newMoment.isAfter(currentMoment);

    const calendarContainer = document.querySelector('.rbc-calendar');
    const mainCalendarGrid = document.querySelector(
      '[style*="transition: all 0.3s ease-in-out"]'
    );

    if (calendarContainer || mainCalendarGrid) {
      const targetElement = calendarContainer || mainCalendarGrid;
      targetElement.style.transition = 'all 0.4s ease-in-out';

      if (isGoingBack) {
        targetElement.style.transform = 'translateX(-50px) scale(0.98)';
      } else if (isGoingForward) {
        targetElement.style.transform = 'translateX(50px) scale(0.98)';
      }
      targetElement.style.opacity = '0.8';

      setTimeout(() => {
        setCurrentDate(moment(date));

        setTimeout(() => {
          targetElement.style.transform = 'translateX(0) scale(1)';
          targetElement.style.opacity = '1';

          setTimeout(() => {
            targetElement.style.transition = 'all 0.3s ease-in-out';
          }, 400);
        }, 100);
      }, 200);
    } else {
      setCurrentDate(moment(date));
    }
  };

  const handleSidebarDateClick = (date, e) => {
    const currentMoment = currentDate;
    const clickedMoment = date;
    const isPastDate = clickedMoment.isBefore(currentMoment, 'day');
    const isFutureDate = clickedMoment.isAfter(currentMoment, 'day');

    const calendarContainer = document.querySelector('.rbc-calendar');
    const mainCalendarGrid = document.querySelector(
      '[style*="transition: all 0.3s ease-in-out"]'
    );

    if (calendarContainer || mainCalendarGrid) {
      const targetElement = calendarContainer || mainCalendarGrid;
      targetElement.style.transition = 'all 0.4s ease-in-out';

      if (isPastDate) {
        targetElement.style.transform = 'translateX(-100px) scale(0.95)';
        targetElement.style.opacity = '0.7';
      } else if (isFutureDate) {
        targetElement.style.transform = 'translateX(100px) scale(0.95)';
        targetElement.style.opacity = '0.7';
      } else {
        targetElement.style.transform = 'scale(0.98)';
        targetElement.style.opacity = '0.8';
      }

      setTimeout(() => {
        setSelectedDate(date);
        setCurrentDate(date);

        setTimeout(() => {
          if (isPastDate) {
            targetElement.style.transform = 'translateX(0) scale(1)';
          } else if (isFutureDate) {
            targetElement.style.transform = 'translateX(0) scale(1)';
          } else {
            targetElement.style.transform = 'scale(1)';
          }
          targetElement.style.opacity = '1';

          setTimeout(() => {
            targetElement.style.transition = 'all 0.3s ease-in-out';
          }, 400);
        }, 200);
      }, 200);
    } else {
      setSelectedDate(date);
      setCurrentDate(date);
    }
  };

  const handleViewChange = newView => {
    setView(newView);
    setShowViewDropdown(false);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'weekends':
        setShowWeekends(value);
        break;
      case 'declined':
        setShowDeclinedEvents(value);
        break;
      case 'completed':
        setShowCompletedTasks(value);
        break;
      default:
        break;
    }
  };

  const handleCreateOption = type => {
    setShowCreateDropdown(false);

    if (type === 'Event') {
      // Use EditLayoutModal for Event/Task creation with current date
      const now = moment.tz('Asia/Kolkata');
      setSelectedSlotDate({
        defaultDueDate: now.format('YYYY-MM-DD'),
        defaultMeetingStartDateTime: now.format('YYYY-MM-DD HH:mm:ss'),
        defaultMeetingEndDateTime: now
          .clone()
          .add(1, 'hour')
          .format('YYYY-MM-DD HH:mm:ss'),
      });
      setEditingRecordId(null);
      setShowEditLayoutModal(true);
      return;
    }

    // FIXED: Use timezone-aware moment to prevent date shifting issues
    const now = moment.tz('Asia/Kolkata');
    const newTaskData = {
      id: null,
      title: '',
      description: '',
      start: now.format('YYYY-MM-DD HH:mm'),
      end: now.clone().add(1, 'hour').format('YYYY-MM-DD HH:mm'),
      priority: 'Normal',
      status: 'Not Started',
      type: type,
      location: '',
      guests: [],
      targetdate: now.toDate(),
      startdatetime: now.toDate(),
      enddatetime: now.clone().add(1, 'hour').toDate(),
    };

    setModalPosition({
      x: window.innerWidth / 2 - 225,
      y: window.innerHeight / 2 - 300,
    });

    setTaskData(newTaskData);
    setShowTaskModal(true);
  };

  const getDaysInMonth = () => {
    const startOfMonth = currentDate.clone().startOf('month');
    const endOfMonth = currentDate.clone().endOf('month');
    const startOfCalendar = startOfMonth.clone().startOf('week');
    const endOfCalendar = endOfMonth.clone().endOf('week');

    const days = [];
    const day = startOfCalendar.clone();

    while (day.isSameOrBefore(endOfCalendar, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  };

  const getEventsForDate = date => {
    // Only Outlook events now
    if (!events || !Array.isArray(events)) {
      return [];
    }
    return events.filter(event => {
      const eventDate = moment(event.start);
      const isSameDay = eventDate.isSame(date, 'day');

      if (!showWeekends && (date.day() === 0 || date.day() === 6)) {
        return false; // Hide weekends
      }

      if (!showDeclinedEvents && event.status === 'Declined') {
        return false; // Hide declined events
      }

      if (!showCompletedTasks && event.status === 'Completed') {
        return false; // Hide completed tasks
      }

      return isSameDay;
    });
  };

  const getEventsForMonth = (month, year) => {
    // Only Outlook events now
    if (!events || !Array.isArray(events)) {
      return [];
    }
    return events.filter(event => {
      const eventDate = moment(event.start);
      const isSameMonth =
        eventDate.month() === month && eventDate.year() === year;

      // Apply filters
      if (!showDeclinedEvents && event.status === 'Declined') {
        return false; // Hide declined events
      }

      if (!showCompletedTasks && event.status === 'Completed') {
        return false; // Hide completed tasks
      }

      return isSameMonth;
    });
  };

  const renderYearView = () => {
    const year = currentDate.year();
    const months = [];

    for (let month = 0; month < 12; month++) {
      const monthDate = moment().year(year).month(month);
      const monthEvents = getEventsForMonth(month, year);

      months.push(
        <div
          key={month}
          style={{
            width: 'calc(25% - 8px)',
            margin: '4px',
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#3c4043',
              marginBottom: '8px',
              textAlign: 'center',
            }}
          >
            {monthDate.format('MMMM')}
          </div>

          {/* Mini calendar grid */}
          <div style={{ fontSize: '10px' }}>
            {/* Days of week header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '1px',
                marginBottom: '2px',
              }}
            >
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <div
                  key={`yearview-day-${index}`}
                  style={{
                    textAlign: 'center',
                    color: '#5f6368',
                    padding: '2px',
                    fontSize: '10px',
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '1px',
              }}
            >
              {(() => {
                const startOfMonth = monthDate.clone().startOf('month');
                const endOfMonth = monthDate.clone().endOf('month');
                const startOfCalendar = startOfMonth.clone().startOf('week');
                const endOfCalendar = endOfMonth.clone().endOf('week');

                const days = [];
                const day = startOfCalendar.clone();

                while (day.isSameOrBefore(endOfCalendar, 'day')) {
                  const isCurrentMonth = day.isSame(monthDate, 'month');
                  const isToday = day.isSame(moment(), 'day');
                  const dayEvents = getEventsForDate(day);
                  const hasEvents = dayEvents.length > 0;
                  const eventCount = dayEvents.length;

                  days.push(
                    <div
                      key={day.format('YYYY-MM-DD')}
                      style={{
                        position: 'relative',
                        textAlign: 'center',
                        padding: '4px 2px',
                        color: isCurrentMonth ? '#3c4043' : '#5f6368',
                        backgroundColor: isToday ? '#1a73e8' : 'transparent',
                        color: isToday
                          ? 'white'
                          : isCurrentMonth
                            ? '#3c4043'
                            : '#5f6368',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '10px',
                        minHeight: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease-in-out',
                        transform: 'scale(1)',
                        border:
                          hasEvents && !isToday ? '1px solid #e8f0fe' : 'none',
                      }}
                      onClick={() => {
                        if (hasEvents) {
                          setTaskData({
                            targetdate: day.toDate(),
                            startdatetime: dayEvents[0].start,
                            enddatetime: dayEvents[0].end,
                            title: dayEvents[0].title,
                            description: dayEvents[0].description || '',
                            status: dayEvents[0].status || 'Scheduled',
                            priority: dayEvents[0].priority || 'Normal',
                            location: dayEvents[0].location || '',
                            guests: dayEvents[0].guests || [],
                            isOutlookEvent:
                              dayEvents[0].isOutlookEvent || false,
                            outlookEventId: dayEvents[0].outlookEventId,
                          });
                          setShowTaskModal(true);
                        } else {
                          handleSidebarDateClick(day);
                        }
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.transform = 'scale(1.15)';
                        e.currentTarget.style.backgroundColor = isToday
                          ? '#1557b0'
                          : hasEvents
                            ? '#e8f0fe'
                            : '#f8f9fa';
                        e.currentTarget.style.boxShadow =
                          '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.backgroundColor = isToday
                          ? '#1a73e8'
                          : 'transparent';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div>{day.date()}</div>
                      {hasEvents && (
                        <div
                          style={{
                            display: 'flex',
                            gap: '1px',
                            marginTop: '2px',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            maxWidth: '100%',
                          }}
                        >
                          {dayEvents.slice(0, 3).map((evt, idx) => (
                            <div
                              key={idx}
                              style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: isToday ? 'white' : '#0078d4',
                                boxShadow: isToday
                                  ? '0 0 2px rgba(255,255,255,0.8)'
                                  : '0 0 3px rgba(0,120,212,0.8)',
                                border: isToday
                                  ? '1px solid rgba(255,255,255,0.3)'
                                  : '1px solid rgba(0,120,212,0.3)',
                              }}
                              title={evt.title}
                            />
                          ))}
                          {eventCount > 3 && (
                            <div
                              style={{
                                fontSize: '7px',
                                color: isToday ? 'white' : '#0078d4',
                                fontWeight: 'bold',
                                marginLeft: '1px',
                              }}
                            >
                              +{eventCount - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  day.add(1, 'day');
                }

                return days;
              })()}
            </div>
          </div>

          {/* Month events summary */}
          {monthEvents.length > 0 && (
            <div
              style={{
                marginTop: '8px',
                padding: '4px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
              }}
            >
              <div
                style={{
                  fontSize: '10px',
                  color: '#5f6368',
                  textAlign: 'center',
                }}
              >
                {monthEvents.length} event{monthEvents.length > 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          padding: '16px',
          gap: '8px',
        }}
      >
        {months}
      </div>
    );
  };

  const days = getDaysInMonth();
  const weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          height: '100vh',
          backgroundColor: '#f8f9fa',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
        }}
      >
        <div style={{ fontSize: '18px', color: '#3c4043' }}>
          Loading Calendar...
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: '#f8f9fa',
        fontFamily: 'Google Sans, Roboto, Arial, sans-serif',
      }}
    >
      {/* Left Sidebar */}
      <div
        style={{
          width: '280px',
          backgroundColor: 'white',
          borderRight: '1px solid #dadce0',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          boxShadow: '2px 0 4px rgba(0,0,0,0.05)',
        }}
      >
        {/* Create Button with Dropdown */}
        <div
          style={{ position: 'relative', marginBottom: '24px' }}
          data-dropdown
        >
          <button
            onClick={() => setShowCreateDropdown(!showCreateDropdown)}
            style={{
              width: '100%',
              height: '40px',
              backgroundColor: '#1a73e8',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(26, 115, 232, 0.3)',
            }}
            onMouseOver={e => {
              e.target.style.backgroundColor = '#1557b0';
            }}
            onMouseOut={e => {
              e.target.style.backgroundColor = '#1a73e8';
            }}
          >
            <span style={{ fontSize: '18px' }}>+</span>
            Create
          </button>

          {/* Create Dropdown */}
          {showCreateDropdown && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #dadce0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                marginTop: '4px',
              }}
            >
              <div
                onClick={() => handleCreateOption('Event')}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#3c4043',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'background-color 0.2s ease',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                }}
                onMouseOver={e => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={e => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#1a73e8',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                  }}
                >
                  📅
                </div>
                Event
              </div>

              <div
                onClick={() => handleCreateOption('Task')}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#3c4043',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseOver={e => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={e => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#34a853',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                  }}
                >
                  ✓
                </div>
                Task
              </div>

              <div
                onClick={() => handleCreateOption('Out of office')}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#3c4043',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseOver={e => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={e => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#ea4335',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                  }}
                >
                  🚫
                </div>
                Out of office
              </div>

              <div
                onClick={() => handleCreateOption('Appointment schedule')}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#3c4043',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'background-color 0.2s ease',
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px',
                }}
                onMouseOver={e => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={e => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#fbbc04',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                  }}
                >
                  📋
                </div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  Appointment schedule
                  <span
                    style={{
                      backgroundColor: '#1a73e8',
                      color: 'white',
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontWeight: '500',
                    }}
                  >
                    New
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mini Calendar */}
        <div style={{ marginBottom: '24px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <span
              style={{ fontSize: '14px', fontWeight: '500', color: '#3c4043' }}
            >
              {currentDate.format('MMMM YYYY')}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() =>
                  setCurrentDate(currentDate.clone().subtract(1, 'month'))
                }
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                }}
              >
                ←
              </button>
              <button
                onClick={() =>
                  setCurrentDate(currentDate.clone().add(1, 'month'))
                }
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                }}
              >
                →
              </button>
            </div>
          </div>

          {/* Mini Calendar Grid */}
          <div style={{ fontSize: '12px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '2px',
                marginBottom: '4px',
              }}
            >
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <div
                  key={`sidebar-day-${index}`}
                  style={{
                    textAlign: 'center',
                    color: '#5f6368',
                    padding: '4px',
                  }}
                >
                  {day}
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '2px',
              }}
            >
              {Array.from({ length: 35 }, (_, i) => {
                const day = currentDate
                  .clone()
                  .startOf('month')
                  .startOf('week')
                  .add(i, 'days');
                const isCurrentMonth = day.isSame(currentDate, 'month');
                const isToday = day.isSame(moment(), 'day');
                const isSelected = day.isSame(selectedDate, 'day');

                return (
                  <div
                    key={i}
                    onClick={e => handleSidebarDateClick(day, e)}
                    style={{
                      textAlign: 'center',
                      padding: '4px',
                      color: isCurrentMonth ? '#3c4043' : '#5f6368',
                      backgroundColor: isToday
                        ? '#1a73e8'
                        : isSelected
                          ? '#e8f0fe'
                          : 'transparent',
                      color: isToday
                        ? 'white'
                        : isCurrentMonth
                          ? '#3c4043'
                          : '#5f6368',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      transform: 'scale(1)',
                      fontSize: '12px',
                      minHeight: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseOver={e => {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.backgroundColor = isToday
                        ? '#1557b0'
                        : '#f8f9fa';
                    }}
                    onMouseOut={e => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.backgroundColor = isToday
                        ? '#1a73e8'
                        : isSelected
                          ? '#e8f0fe'
                          : 'transparent';
                    }}
                  >
                    {day.date()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Meet with Section */}
        <div style={{ marginBottom: '16px', position: 'relative' }} data-search>
          <div
            style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#3c4043',
              marginBottom: '8px',
            }}
          >
            Meet with...
          </div>
          <input
            type='text'
            placeholder='Search for people or enter email address'
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              handleSearchPeople(e.target.value);
            }}
            onFocus={() => {
              if (searchResults.length > 0) {
                setShowSearchResults(true);
              }
            }}
            onKeyPress={e => {
              if (e.key === 'Enter' && searchQuery.trim()) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (emailRegex.test(searchQuery.trim())) {
                  const emailParts = searchQuery.trim().split('@');
                  const name = emailParts[0]
                    .replace(/[._]/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());

                  const now = moment.tz('Asia/Kolkata');

                  const newTaskData = {
                    id: null,
                    title: `Meeting with ${name}`,
                    description: '',
                    start: now.format('YYYY-MM-DD HH:mm'),
                    end: now.clone().add(1, 'hour').format('YYYY-MM-DD HH:mm'),
                    priority: 'Normal',
                    status: 'Not Started',
                    type: 'Event',
                    location: '',
                    guests: [searchQuery.trim()],
                    targetdate: now.toDate(),
                    startdatetime: now.toDate(),
                    enddatetime: now.clone().add(1, 'hour').toDate(),
                  };

                  setModalPosition({
                    x: window.innerWidth / 2 - 225,
                    y: window.innerHeight / 2 - 300,
                  });

                  setTaskData(newTaskData);
                  setShowTaskModal(true);
                  setSearchQuery('');
                  setShowSearchResults(false);
                }
              }
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #dadce0',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
            }}
          />

          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #dadce0',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto',
                marginTop: '2px',
              }}
            >
              {searchResults.map(person => (
                <div
                  key={person.id}
                  onClick={() => handleSelectPerson(person)}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseOver={e => {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseOut={e => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  {/* Profile Photo */}
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundColor: '#e8f0fe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {person.photo ? (
                      <img
                        src={person.photo}
                        alt={person.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={e => {
                          // Fallback to avatar if image fails to load
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#1a73e8',
                        color: 'white',
                        display: person.photo ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      {person.avatar}
                    </div>
                  </div>

                  {/* Person Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#3c4043',
                        marginBottom: '2px',
                      }}
                    >
                      {person.name}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#5f6368',
                        marginBottom: '2px',
                      }}
                    >
                      {person.email}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#1a73e8',
                        fontWeight: '500',
                      }}
                    >
                      {person.position} • {person.department}
                    </div>
                  </div>

                  {/* Company Badge */}
                  {person.email.includes('@ibirdsservices.com') && (
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#1a73e8',
                        backgroundColor: '#e8f0fe',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontWeight: '500',
                      }}
                    >
                      iBirds
                    </div>
                  )}

                  {/* Dynamic Email Badge */}
                  {person.isDynamic && (
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#34a853',
                        backgroundColor: '#e6f4ea',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontWeight: '500',
                      }}
                    >
                      New
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Color Legend */}
        <div
          style={{
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #dadce0',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#3c4043',
              marginBottom: '12px',
            }}
          >
            Task Types
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: '#90CAF9',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#5f6368' }}>Call</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: '#CE93D8',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#5f6368' }}>
                Meeting
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: '#FFF59D',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#5f6368' }}>Email</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: '#80DEEA',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#5f6368' }}>
                Follow-up
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: '#FFE082',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#5f6368' }}>
                Reminder
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: '#81C784',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#5f6368' }}>
                WhatsApp
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '3px',
                  backgroundColor: '#B0BEC5',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              ></div>
              <span style={{ fontSize: '13px', color: '#5f6368' }}>Other</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div
          style={{
            height: '64px',
            backgroundColor: 'white',
            borderBottom: '1px solid #dadce0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: '#1a73e8',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 1px 3px rgba(26, 115, 232, 0.3)',
                }}
              >
                14
              </div>
              <span
                style={{
                  fontSize: '22px',
                  fontWeight: '400',
                  color: '#202124',
                  letterSpacing: '-0.2px',
                }}
              >
                Calendar
              </span>
            </div>
            <button
              onClick={() => {
                setCurrentDate(moment());
                setSelectedDate(moment());
              }}
              style={{
                padding: '10px 16px',
                border: '1px solid #dadce0',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#3c4043',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
              onMouseOver={e => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#5f6368';
              }}
              onMouseOut={e => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#dadce0';
              }}
            >
              Today
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() =>
                  setCurrentDate(
                    currentDate
                      .clone()
                      .subtract(1, view === Views.YEAR ? 'year' : 'month')
                  )
                }
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '10px',
                  borderRadius: '50%',
                  color: '#5f6368',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={e => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={e => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                ←
              </button>
              <span
                style={{
                  fontSize: '20px',
                  fontWeight: '400',
                  color: '#202124',
                  minWidth: '140px',
                  textAlign: 'center',
                  letterSpacing: '-0.2px',
                }}
              >
                {view === Views.YEAR
                  ? currentDate.format('YYYY')
                  : currentDate.format('MMMM YYYY')}
              </span>
              <button
                onClick={() =>
                  setCurrentDate(
                    currentDate
                      .clone()
                      .add(1, view === Views.YEAR ? 'year' : 'month')
                  )
                }
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '10px',
                  borderRadius: '50%',
                  color: '#5f6368',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={e => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={e => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                →
              </button>
            </div>

            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowViewDropdown(!showViewDropdown)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#1a73e8',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 3px rgba(26, 115, 232, 0.3)',
                }}
                onMouseOver={e => {
                  e.target.style.backgroundColor = '#1557b0';
                }}
                onMouseOut={e => {
                  e.target.style.backgroundColor = '#1a73e8';
                }}
              >
                {view === Views.DAY
                  ? 'Day'
                  : view === Views.WEEK
                    ? 'Week'
                    : view === Views.MONTH
                      ? 'Month'
                      : view === Views.YEAR
                        ? 'Year'
                        : 'Month'}
                <span style={{ fontSize: '12px' }}>▼</span>
              </button>

              {showViewDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: 'white',
                    border: '1px solid #dadce0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '140px',
                    marginTop: '4px',
                  }}
                >
                  {[
                    { key: Views.DAY, label: 'Day', shortcut: 'D' },
                    { key: Views.WEEK, label: 'Week', shortcut: 'W' },
                    { key: Views.MONTH, label: 'Month', shortcut: 'M' },
                    { key: Views.YEAR, label: 'Year', shortcut: 'Y' },
                    { key: Views.AGENDA, label: 'Schedule', shortcut: 'A' },
                  ].map(({ key, label, shortcut }) => (
                    <div
                      key={key}
                      onClick={() => handleViewChange(key)}
                      style={{
                        padding: '10px 16px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#3c4043',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor:
                          view === key ? '#e8f0fe' : 'transparent',
                        transition: 'background-color 0.2s ease',
                      }}
                      onMouseOver={e => {
                        if (view !== key) {
                          e.target.style.backgroundColor = '#f8f9fa';
                        }
                      }}
                      onMouseOut={e => {
                        if (view !== key) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <span
                        style={{ fontWeight: view === key ? '500' : '400' }}
                      >
                        {label}
                      </span>
                      <span style={{ fontSize: '12px', color: '#5f6368' }}>
                        ({shortcut})
                      </span>
                    </div>
                  ))}

                  <div
                    style={{ borderTop: '1px solid #dadce0', margin: '8px 0' }}
                  ></div>

                  <div style={{ padding: '12px 16px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '14px',
                        color: '#3c4043',
                        marginBottom: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type='checkbox'
                        checked={showWeekends}
                        onChange={e =>
                          handleFilterChange('weekends', e.target.checked)
                        }
                        style={{ margin: 0 }}
                      />
                      Show weekends
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '14px',
                        color: '#3c4043',
                        marginBottom: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type='checkbox'
                        checked={showDeclinedEvents}
                        onChange={e =>
                          handleFilterChange('declined', e.target.checked)
                        }
                        style={{ margin: 0 }}
                      />
                      Show declined events
                    </label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '14px',
                        color: '#3c4043',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type='checkbox'
                        checked={showCompletedTasks}
                        onChange={e =>
                          handleFilterChange('completed', e.target.checked)
                        }
                        style={{ margin: 0 }}
                      />
                      Show completed tasks
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        fontSize: '14px',
                        color: '#3c4043',
                        marginTop: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#e8f0fe',
                        borderRadius: '4px',
                      }}
                    >
                      <span style={{ color: '#0078d4', fontWeight: '500' }}>
                        📧
                      </span>
                      <span>
                        Showing Outlook Calendar ({events.length} events)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {/* Outlook Sync Status */}
              {outlookSyncStatus && (
                <div
                  style={{
                    padding: '6px 12px',
                    backgroundColor: outlookSyncStatus.includes('✓')
                      ? '#e6f4ea'
                      : outlookSyncStatus.includes('⚠')
                        ? '#fef7e0'
                        : '#e8f0fe',
                    color: outlookSyncStatus.includes('✓')
                      ? '#1e8e3e'
                      : outlookSyncStatus.includes('⚠')
                        ? '#ea8600'
                        : '#1a73e8',
                    borderRadius: '4px',
                    fontSize: '12px',
                    marginRight: '8px',
                  }}
                >
                  {outlookSyncStatus}
                </div>
              )}

              {/* Google Sync Status */}
              {googleSyncStatus && (
                <div
                  style={{
                    padding: '6px 12px',
                    backgroundColor: googleSyncStatus.includes('✓')
                      ? '#e6f4ea'
                      : googleSyncStatus.includes('⚠')
                        ? '#fef7e0'
                        : '#e8f0fe',
                    color: googleSyncStatus.includes('✓')
                      ? '#1e8e3e'
                      : googleSyncStatus.includes('⚠')
                        ? '#ea8600'
                        : '#1a73e8',
                    borderRadius: '4px',
                    fontSize: '12px',
                    marginRight: '8px',
                  }}
                >
                  {googleSyncStatus}
                </div>
              )}

              {/* Outlook Account Selector */}
              {outlookAccounts.length > 0 && (
                <div style={{ position: 'relative', marginRight: '8px' }}>
                  <button
                    onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                    style={{
                      border: '1px solid #dadce0',
                      background: 'white',
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      color: '#333',
                      fontSize: '12px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    📧{' '}
                    {selectedOutlookAccount
                      ? selectedOutlookAccount.name
                      : 'Select Account'}
                    <span style={{ fontSize: '10px' }}>▼</span>
                  </button>

                  {showAccountDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '4px',
                        background: 'white',
                        border: '1px solid #dadce0',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        zIndex: 1000,
                        minWidth: '200px',
                      }}
                    >
                      {outlookAccounts.map(account => (
                        <div
                          key={account.id}
                          onClick={() => {
                            setSelectedOutlookAccount(account);
                            setShowAccountDropdown(false);
                            localStorage.setItem(
                              'selectedOutlookAccountId',
                              account.id
                            );
                            if (showOutlookEvents) {
                              fetchOutlookEvents();
                            }
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            borderBottom: '1px solid #f0f0f0',
                            background:
                              selectedOutlookAccount?.id === account.id
                                ? '#e8f4fd'
                                : 'white',
                            color:
                              selectedOutlookAccount?.id === account.id
                                ? '#0078d4'
                                : '#333',
                            fontWeight:
                              selectedOutlookAccount?.id === account.id
                                ? '600'
                                : '400',
                          }}
                          onMouseOver={e => {
                            if (selectedOutlookAccount?.id !== account.id) {
                              e.target.style.background = '#f5f8fb';
                            }
                          }}
                          onMouseOut={e => {
                            if (selectedOutlookAccount?.id !== account.id) {
                              e.target.style.background = 'white';
                            }
                          }}
                        >
                          <div style={{ fontWeight: '500' }}>
                            {account.name}
                          </div>
                          {account.smtp_mail && (
                            <div
                              style={{
                                fontSize: '10px',
                                color: '#666',
                                marginTop: '2px',
                              }}
                            >
                              {account.smtp_mail}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Calendar Selection Buttons */}
              <div style={{ display: 'flex', gap: '6px', marginRight: '8px' }}>
                {/* Outlook Login Button */}
                {/* <button
                  onClick={() => handleSyncOutlook()}
                  disabled={isSyncingGoogle || isSyncingOutlook}
                  style={{
                    border: showOutlookEvents
                      ? '2px solid #0078d4'
                      : '1px solid #dadce0',
                    background: showOutlookEvents ? '#e8f4fd' : 'white',
                    cursor:
                      isSyncingGoogle || isSyncingOutlook
                        ? 'not-allowed'
                        : 'pointer',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    color: showOutlookEvents ? '#0078d4' : '#333',
                    fontSize: '12px',
                    fontWeight: showOutlookEvents ? '600' : '500',
                    opacity: isSyncingGoogle || isSyncingOutlook ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseOver={e => {
                    if (
                      !isSyncingGoogle &&
                      !isSyncingOutlook &&
                      !showOutlookEvents
                    ) {
                      e.target.style.borderColor = '#0078d4';
                      e.target.style.background = '#f5f8fb';
                    }
                  }}
                  onMouseOut={e => {
                    if (
                      !isSyncingGoogle &&
                      !isSyncingOutlook &&
                      !showOutlookEvents
                    ) {
                      e.target.style.borderColor = '#dadce0';
                      e.target.style.background = 'white';
                    }
                  }}
                >
                  📧 Outlook
                  {isSyncingOutlook && (
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid #0078d4',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginLeft: '2px',
                      }}
                    ></div>
                  )}
                </button> */}

                {/* Google Login Button */}
                {/* <button
                  onClick={() => handleSyncGoogle()}
                  disabled={isSyncingGoogle || isSyncingOutlook}
                  style={{
                    border: showGoogleEvents
                      ? '2px solid #34a853'
                      : '1px solid #dadce0',
                    background: showGoogleEvents ? '#e6f4ea' : 'white',
                    cursor:
                      isSyncingGoogle || isSyncingOutlook
                        ? 'not-allowed'
                        : 'pointer',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    color: showGoogleEvents ? '#34a853' : '#333',
                    fontSize: '12px',
                    fontWeight: showGoogleEvents ? '600' : '500',
                    opacity: isSyncingGoogle || isSyncingOutlook ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseOver={e => {
                    if (
                      !isSyncingGoogle &&
                      !isSyncingOutlook &&
                      !showGoogleEvents
                    ) {
                      e.target.style.borderColor = '#34a853';
                      e.target.style.background = '#f5fbf7';
                    }
                  }}
                  onMouseOut={e => {
                    if (
                      !isSyncingGoogle &&
                      !isSyncingOutlook &&
                      !showGoogleEvents
                    ) {
                      e.target.style.borderColor = '#dadce0';
                      e.target.style.background = 'white';
                    }
                  }}
                >
                  🔄 Google
                  {localStorage.getItem('googleUserInfo') && (
                    <span style={{ fontSize: '10px', opacity: 0.7 }}>
                      (
                      {JSON.parse(localStorage.getItem('googleUserInfo'))
                        .email?.split('@')[0]
                        ?.substring(0, 6) || 'User'}
                      )
                    </span>
                  )}
                  {isSyncingGoogle && (
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid #34a853',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginLeft: '2px',
                      }}
                    ></div>
                  )}
                </button> */}
              </div>
            </div>

            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#1a73e8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(26, 115, 232, 0.3)',
              }}
              onMouseOver={e => {
                e.target.style.backgroundColor = '#1557b0';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={e => {
                e.target.style.backgroundColor = '#1a73e8';
                e.target.style.transform = 'scale(1)';
              }}
            >
              M
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div
          style={{
            flex: 1,
            padding: '16px',
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {view === Views.YEAR ? (
            <div
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                height: '100%',
                overflow: 'auto',
                transition: 'all 0.3s ease-in-out',
                transform: 'translateZ(0)',
              }}
            >
              {renderYearView()}
            </div>
          ) : (
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
                overflow: 'hidden',
                transition: 'all 0.3s ease-in-out',
                transform: 'translateZ(0)',
              }}
            >
              <div
                style={{
                  transition: 'all 0.3s ease-in-out',
                  transform: 'translateZ(0)',
                  opacity: 1,
                }}
              >
                <Calendar
                  localizer={localizer}
                  events={(() => {
                    // Merge and de-duplicate events before rendering
                    const unique = new Map();

                    // Index external events first by stable key
                    for (const ev of [...outlookEvents, ...googleEvents]) {
                      const key = `${ev.resource?.source || ev.source || 'ext'}-${(ev.id || '').replace(/^(google-|outlook-)/, '')}`;
                      if (!unique.has(key)) unique.set(key, ev);
                    }

                    // Add CRM tasks only if not linked to an existing external
                    for (const ev of crmTasks) {
                      const src = ev.resource?.parent_reference_object;
                      const refId = ev.resource?.parent_reference_id;
                      const linkKey = src && refId ? `${src}-${refId}` : null;

                      if (linkKey && unique.has(linkKey)) {
                        continue; // skip, already represented by external event
                      }

                      // Fallback heuristic de-dupe by title+start minute
                      const startTs = new Date(ev.start).getTime();
                      const fallbackKey = `fb-${(ev.title || '').toLowerCase().trim()}-${Math.floor(startTs / 60000)}`;
                      if (unique.has(fallbackKey)) continue;
                      unique.set(linkKey || fallbackKey, ev);
                    }

                    return Array.from(unique.values());
                  })()}
                  startAccessor='start'
                  endAccessor='end'
                  style={{
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                  }}
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={handleSelectEvent}
                  selectable
                  eventPropGetter={eventStyleGetter}
                  view={view}
                  onView={setView}
                  date={currentDate.toDate()}
                  onNavigate={handleNavigate}
                  views={[Views.DAY, Views.WEEK, Views.MONTH, Views.AGENDA]}
                  step={15}
                  timeslots={4}
                  min={new Date(2024, 0, 1, 6, 0)}
                  max={new Date(2024, 0, 1, 22, 0)}
                  showMultiDayTimes
                  showWeekends={showWeekends}
                  popup
                  components={{
                    month: {
                      dateHeader: ({ label, date }) => (
                        <div
                          style={{
                            padding: '4px 8px',
                            fontWeight: '500',
                            fontSize: '14px',
                            color: '#3c4043',
                          }}
                        >
                          {label}
                        </div>
                      ),
                      dateCellWrapper: ({ children, value }) => (
                        <div
                          style={{
                            minHeight: '80px',
                            border: '1px solid #e8eaed',
                            backgroundColor: 'white',
                            position: 'relative',
                          }}
                        >
                          {children}
                        </div>
                      ),
                    },
                  }}
                  formats={{
                    monthHeaderFormat: 'MMMM YYYY',
                    dayFormat: 'D',
                    dayHeaderFormat: 'ddd',
                    dayRangeHeaderFormat: ({ start, end }) =>
                      `${moment(start).format('MMM D')} - ${moment(end).format('MMM D, YYYY')}`,
                    eventTimeRangeFormat: ({ start, end }) =>
                      `${moment(start).format('h:mm A')} - ${moment(end).format('h:mm A')}`,
                  }}
                  culture='en-US'
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CalendarTaskModal removed - Now using TaskEdit modal for all operations */}

      <style>
        {' '}
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Calendar Month View Styling */
          .rbc-calendar {
            font-family: 'Google Sans', 'Roboto', 'Arial', sans-serif;
          }

          .rbc-month-view {
            height: 100% !important;
          }

          .rbc-month-row {
            min-height: 80px !important;
          }

          .rbc-date-cell {
            padding: 4px 8px !important;
            font-weight: 500 !important;
            font-size: 14px !important;
            color: #3c4043 !important;
          }

          .rbc-off-range-bg {
            background-color: #f8f9fa !important;
          }

          .rbc-today {
            background-color: #e8f0fe !important;
          }

          .rbc-event {
            border-radius: 4px !important;
            font-size: 12px !important;
            font-weight: 500 !important;
            padding: 2px 4px !important;
            margin: 1px !important;
          }

          .rbc-event-content {
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }

          .rbc-event-label {
            font-size: 11px !important;
          }

          /* Ensure proper grid display */
          .rbc-month-header {
            background-color: #f8f9fa !important;
            border-bottom: 1px solid #dadce0 !important;
          }

          .rbc-header {
            padding: 8px 4px !important;
            font-weight: 500 !important;
            font-size: 12px !important;
            color: #5f6368 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
          }

          /* Fix empty month view */
          .rbc-month-view .rbc-row {
            border-bottom: 1px solid #e8eaed !important;
          }

          .rbc-month-view .rbc-row:last-child {
            border-bottom: none !important;
          }

          .rbc-month-view .rbc-row .rbc-row-content {
            border-right: 1px solid #e8eaed !important;
          }

          .rbc-month-view .rbc-row .rbc-row-content:last-child {
            border-right: none !important;
          }
        `}
      </style>

      {/* EditLayoutModal - Dynamic Task Modal */}
      <EditLayoutModal
        show={showEditLayoutModal}
        onHide={() => {
          setShowEditLayoutModal(false);
          setEditingRecordId(null);
          setSelectedSlotDate(null);
        }}
        objectName='task'
        id={editingRecordId}
        queryParams={selectedSlotDate}
        sourcePage='calendar'
        onSave={async savedData => {
          console.log('✅ Task saved in DB:', savedData);

          // Extract task data
          const taskData = savedData?.data || savedData;

          console.log('📅 Task date fields:');
          console.log('   due_date:', taskData.due_date);
          console.log(
            '   meeting_start_datetime:',
            taskData.meeting_start_datetime
          );
          console.log(
            '   meeting_end_datetime:',
            taskData.meeting_end_datetime
          );

          // ✅ Check if this is CREATE or EDIT
          const isEditing = editingRecordId ? true : false;

          if (!isEditing) {
            // ===== CREATE NEW EVENT =====
            console.log('🆕 Creating NEW event...');
            try {
              console.log('🔍 Checking calendar config...');
              console.log('   showGoogleEvents:', showGoogleEvents);
              console.log('   showOutlookEvents:', showOutlookEvents);
              console.log('   hasMailConfig:', hasMailConfig);

              // Check if we have Google Calendar access
              // Backend will use database credentials (mail_config table)
              if (showGoogleEvents && taskData) {
                console.log(
                  '🔄 Creating event in Google Calendar using DB credentials...'
                );

                const startMoment = taskData.meeting_start_datetime
                  ? moment(taskData.meeting_start_datetime).tz('Asia/Kolkata')
                  : moment().tz('Asia/Kolkata');

                const endMoment = taskData.meeting_end_datetime
                  ? moment(taskData.meeting_end_datetime).tz('Asia/Kolkata')
                  : startMoment.clone().add(1, 'hour');

                const googleEventData = {
                  summary: taskData.title || 'New Event',
                  description: taskData.description || '',
                  start: {
                    dateTime: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: 'Asia/Kolkata',
                  },
                  end: {
                    dateTime: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: 'Asia/Kolkata',
                  },
                };

                console.log(
                  '📤 Sending to Google Calendar API:',
                  googleEventData
                );
                const response =
                  await googleCalendarApi.createEvent(googleEventData);
                console.log('📥 Google Calendar response:', response);

                if (response.success && response.data?.id) {
                  const googleEventId = response.data.id;
                  console.log(
                    '✅ Event created in Google Calendar! ID:',
                    googleEventId
                  );

                  // ✅ UPDATE task in DB with Google event link
                  try {
                    const taskApi = new DataApi('tasks');
                    const taskId = taskData.id;

                    await taskApi.update(taskId, {
                      parent_reference_id: googleEventId,
                      parent_reference_object: 'google',
                      source: 'google',
                    });
                    console.log(
                      '✅ Task updated with Google event link:',
                      googleEventId
                    );
                  } catch (updateError) {
                    console.error(
                      '⚠️ Failed to update task with Google link:',
                      updateError
                    );
                  }

                  alert('✅ Event created in Google Calendar & CRM!');

                  // ✅ Refresh both Google events and CRM tasks
                  setTimeout(() => {
                    console.log('🔄 Refreshing Google Calendar events...');
                    handleSyncGoogle();
                  }, 500);

                  setTimeout(() => {
                    console.log('🔄 Refreshing CRM tasks...');
                    fetchCrmTasks();
                  }, 1500);
                } else {
                  console.error('❌ Failed to create Google event:', response);
                  alert(
                    '⚠️ Task saved in CRM, but failed to create Google Calendar event'
                  );
                  // Still refresh CRM to show the task
                  setTimeout(() => fetchCrmTasks(), 500);
                }
              }
              // Check if we have Outlook access
              else if (showOutlookEvents && taskData) {
                console.log(
                  '🔄 Creating event in Outlook Calendar using DB credentials...'
                );

                const startMoment = taskData.meeting_start_datetime
                  ? moment(taskData.meeting_start_datetime).tz('Asia/Kolkata')
                  : moment().tz('Asia/Kolkata');

                const endMoment = taskData.meeting_end_datetime
                  ? moment(taskData.meeting_end_datetime).tz('Asia/Kolkata')
                  : startMoment.clone().add(1, 'hour');

                const outlookEventData = {
                  subject: taskData.title || 'New Event',
                  body: {
                    contentType: 'HTML',
                    content: taskData.description || '',
                  },
                  start: {
                    dateTime: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: 'Asia/Kolkata',
                  },
                  end: {
                    dateTime: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
                    timeZone: 'Asia/Kolkata',
                  },
                };

                console.log('📤 Sending to Outlook API:', outlookEventData);
                const response = await outlookApi.createEvent(outlookEventData);
                console.log('📥 Outlook response:', response);

                if (response.success && response.event?.id) {
                  const outlookEventId = response.event.id;
                  console.log(
                    '✅ Event created in Outlook Calendar! ID:',
                    outlookEventId
                  );

                  // ✅ UPDATE task in DB with Outlook event link
                  try {
                    const taskApi = new DataApi('tasks');
                    const taskId = taskData.id;

                    await taskApi.update(taskId, {
                      parent_reference_id: outlookEventId,
                      parent_reference_object: 'outlook',
                      source: 'outlook',
                    });
                    console.log(
                      '✅ Task updated with Outlook event link:',
                      outlookEventId
                    );
                  } catch (updateError) {
                    console.error(
                      '⚠️ Failed to update task with Outlook link:',
                      updateError
                    );
                  }

                  alert('✅ Event created in Outlook Calendar & CRM!');

                  // ✅ Refresh both Outlook events and CRM tasks
                  setTimeout(() => {
                    console.log('🔄 Refreshing Outlook Calendar events...');
                    fetchOutlookEvents();
                  }, 500);

                  setTimeout(() => {
                    console.log('🔄 Refreshing CRM tasks...');
                    fetchCrmTasks();
                  }, 1500);
                } else {
                  console.error('❌ Failed to create Outlook event:', response);
                  alert(
                    '⚠️ Task saved in CRM, but failed to create Outlook event'
                  );
                  // Still refresh CRM to show the task
                  setTimeout(() => fetchCrmTasks(), 500);
                }
              } else {
                console.log(
                  'ℹ️ No external calendar configured - DB task only'
                );
                // Refresh CRM tasks to show the new task
                setTimeout(() => {
                  console.log(
                    '🔄 Refreshing CRM tasks (no external calendar)...'
                  );
                  fetchCrmTasks();
                }, 500);
              }
            } catch (error) {
              console.error(
                '❌ Error creating external calendar event:',
                error
              );
              console.error(
                'Error details:',
                error.response?.data || error.message
              );
              alert(
                `⚠️ Task saved in CRM, but external calendar error: ${error.response?.data?.message || error.message}`
              );
              // Still refresh to show DB task
              setTimeout(() => fetchCrmTasks(), 500);
            }
          } else {
            // ===== EDIT EXISTING EVENT =====
            console.log('✏️ Editing EXISTING event...');
            console.log(
              '   Full task data:',
              JSON.stringify(taskData, null, 2)
            );
            console.log(
              '   parent_reference_id:',
              taskData.parent_reference_id
            );
            console.log(
              '   parent_reference_object:',
              taskData.parent_reference_object
            );
            console.log('   showGoogleEvents:', showGoogleEvents);
            console.log('   showOutlookEvents:', showOutlookEvents);

            // Check if task is linked to external calendar
            if (
              taskData.parent_reference_id &&
              taskData.parent_reference_object
            ) {
              console.log(
                '✅ Task is linked to external calendar:',
                taskData.parent_reference_object
              );

              try {
                const startMoment = taskData.meeting_start_datetime
                  ? moment(taskData.meeting_start_datetime).tz('Asia/Kolkata')
                  : moment().tz('Asia/Kolkata');

                const endMoment = taskData.meeting_end_datetime
                  ? moment(taskData.meeting_end_datetime).tz('Asia/Kolkata')
                  : startMoment.clone().add(1, 'hour');

                console.log('📅 Parsed dates for external calendar:');
                console.log(
                  '   Start:',
                  startMoment.format('YYYY-MM-DD HH:mm:ss')
                );
                console.log('   End:', endMoment.format('YYYY-MM-DD HH:mm:ss'));

                // Update Google Calendar event
                if (taskData.parent_reference_object === 'google') {
                  console.log(
                    '🔄 Attempting to update Google Calendar event...'
                  );
                  console.log('   Condition check:');
                  console.log(
                    '   - parent_reference_object === "google":',
                    taskData.parent_reference_object === 'google'
                  );
                  console.log(
                    '   - Event ID to update:',
                    taskData.parent_reference_id
                  );

                  const googleEventData = {
                    summary: taskData.title || 'Updated Event',
                    description: taskData.description || '',
                    start: {
                      dateTime: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
                      timeZone: 'Asia/Kolkata',
                    },
                    end: {
                      dateTime: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
                      timeZone: 'Asia/Kolkata',
                    },
                  };

                  console.log('📤 Sending update to Google Calendar:');
                  console.log('   Event ID:', taskData.parent_reference_id);
                  console.log(
                    '   Event Data:',
                    JSON.stringify(googleEventData, null, 2)
                  );

                  let response;
                  try {
                    response = await googleCalendarApi.updateEvent(
                      taskData.parent_reference_id,
                      googleEventData
                    );
                    console.log(
                      '📥 Google update response:',
                      JSON.stringify(response, null, 2)
                    );
                  } catch (apiError) {
                    console.error('❌ Google Calendar API error:', apiError);
                    console.error(
                      '   Error response:',
                      apiError.response?.data
                    );
                    console.error('   Error message:', apiError.message);
                    throw apiError;
                  }

                  if (response && response.success) {
                    console.log(
                      '✅ SUCCESS! Event updated in Google Calendar!'
                    );
                    console.log('   Updated event data:', response.event);

                    // ✅ CRITICAL: Close modal first, then refresh
                    setShowEditLayoutModal(false);
                    setEditingRecordId(null);
                    setSelectedSlotDate(null);

                    // ✅ Clear existing events to force fresh load
                    console.log('🧹 Clearing ALL events from UI...');
                    setGoogleEvents([]);
                    setCrmTasks([]);
                    setOutlookEvents([]);

                    alert(
                      '✅ Event updated! Calendar will refresh in 6 seconds...'
                    );

                    // ✅ CRITICAL: Google Calendar needs 3-5 seconds to sync on their servers
                    // Step 1: Wait 3 seconds then refresh Google Calendar
                    setTimeout(async () => {
                      console.log(
                        '🔄 Step 1: First Google Calendar refresh (3s delay)...'
                      );
                      await handleSyncGoogle();
                    }, 3000);

                    // Step 2: Wait 5 seconds then refresh again (to catch any delayed syncs)
                    setTimeout(async () => {
                      console.log(
                        '🔄 Step 2: Second Google Calendar refresh (5s delay)...'
                      );
                      await handleSyncGoogle();
                    }, 5000);

                    // Step 3: Wait 6 seconds then refresh CRM tasks
                    setTimeout(async () => {
                      console.log(
                        '🔄 Step 3: Refreshing CRM tasks (6s delay)...'
                      );
                      await fetchCrmTasks();
                    }, 6000);
                  } else {
                    console.error(
                      '❌ Failed to update Google event - invalid response:',
                      response
                    );
                    alert(
                      '⚠️ Task updated in CRM, but failed to update Google Calendar'
                    );
                    // Still refresh to show DB changes
                    setTimeout(() => fetchCrmTasks(), 500);
                  }
                }
                // Update Outlook Calendar event
                else if (taskData.parent_reference_object === 'outlook') {
                  console.log(
                    '🔄 Attempting to update Outlook Calendar event...'
                  );
                  console.log('   Condition check:');
                  console.log(
                    '   - parent_reference_object === "outlook":',
                    taskData.parent_reference_object === 'outlook'
                  );
                  console.log(
                    '   - Event ID to update:',
                    taskData.parent_reference_id
                  );

                  const outlookEventData = {
                    subject: taskData.title || 'Updated Event',
                    body: {
                      contentType: 'HTML',
                      content: taskData.description || '',
                    },
                    start: {
                      dateTime: startMoment.format('YYYY-MM-DDTHH:mm:ss'),
                      timeZone: 'Asia/Kolkata',
                    },
                    end: {
                      dateTime: endMoment.format('YYYY-MM-DDTHH:mm:ss'),
                      timeZone: 'Asia/Kolkata',
                    },
                  };

                  console.log('📤 Sending update to Outlook Calendar:');
                  console.log('   Event ID:', taskData.parent_reference_id);
                  console.log(
                    '   Event Data:',
                    JSON.stringify(outlookEventData, null, 2)
                  );

                  let response;
                  try {
                    response = await outlookApi.updateEvent(
                      taskData.parent_reference_id,
                      outlookEventData
                    );
                    console.log(
                      '📥 Outlook update response:',
                      JSON.stringify(response, null, 2)
                    );
                  } catch (apiError) {
                    console.error('❌ Outlook Calendar API error:', apiError);
                    console.error(
                      '   Error response:',
                      apiError.response?.data
                    );
                    console.error('   Error message:', apiError.message);
                    throw apiError;
                  }

                  if (response && (response.id || response.success)) {
                    setShowEditLayoutModal(false);
                    setEditingRecordId(null);
                    setSelectedSlotDate(null);
                    setOutlookEvents([]);
                    setCrmTasks([]);
                    setGoogleEvents([]);

                    alert(
                      '✅ Event updated! Calendar will refresh in 3 seconds...'
                    );

                    setTimeout(async () => {
                      console.log(
                        '🔄 Step 1: Refreshing Outlook Calendar (2s delay)...'
                      );
                      await fetchOutlookEvents();
                    }, 2000);

                    // Step 2: Wait 3 seconds then refresh again (to catch any delayed syncs)
                    setTimeout(async () => {
                      console.log(
                        '🔄 Step 2: Second Outlook Calendar refresh (3s delay)...'
                      );
                      await fetchOutlookEvents();
                    }, 3000);

                    // Step 3: Wait 4 seconds then refresh CRM tasks
                    setTimeout(async () => {
                      console.log(
                        '🔄 Step 3: Refreshing CRM tasks (4s delay)...'
                      );
                      await fetchCrmTasks();
                    }, 4000);
                  } else {
                    console.error(
                      '❌ Failed to update Outlook event - invalid response:',
                      response
                    );
                    alert(
                      '⚠️ Task updated in CRM, but failed to update Outlook'
                    );
                    // Still refresh to show DB changes
                    setTimeout(() => fetchCrmTasks(), 500);
                  }
                } else {
                  console.log(
                    'ℹ️ External calendar not active or different type - DB update only'
                  );

                  // ✅ Close modal first, then refresh
                  setShowEditLayoutModal(false);
                  setEditingRecordId(null);
                  setSelectedSlotDate(null);

                  // ✅ Clear ALL events from UI
                  console.log('🧹 Clearing ALL events from UI...');
                  setCrmTasks([]);
                  setGoogleEvents([]);
                  setOutlookEvents([]);

                  // ✅ Refresh CRM tasks immediately (DB is fast)
                  setTimeout(async () => {
                    console.log('🔄 Step 1: Refreshing CRM tasks (500ms)...');
                    await fetchCrmTasks();
                  }, 500);

                  // ✅ Second refresh to ensure UI updates
                  setTimeout(async () => {
                    console.log('🔄 Step 2: Second CRM task refresh (1.5s)...');
                    await fetchCrmTasks();
                  }, 1500);
                }
              } catch (error) {
                console.error(
                  '❌ Error updating external calendar event:',
                  error
                );
                console.error(
                  'Error details:',
                  error.response?.data || error.message
                );
                alert(
                  `⚠️ Task updated in CRM, but external calendar error: ${error.response?.data?.message || error.message}`
                );

                // ✅ Close modal and refresh DB changes
                setShowEditLayoutModal(false);
                setEditingRecordId(null);
                setSelectedSlotDate(null);

                // ✅ Clear and refresh CRM tasks
                console.log('🧹 Clearing events due to error...');
                setCrmTasks([]);
                setGoogleEvents([]);
                setOutlookEvents([]);

                setTimeout(async () => {
                  console.log('🔄 Refreshing CRM tasks after error...');
                  await fetchCrmTasks();
                }, 500);

                setTimeout(async () => {
                  console.log('🔄 Second refresh after error...');
                  await fetchCrmTasks();
                }, 1500);
              }
            } else {
              console.log(
                'ℹ️ Task not linked to external calendar - DB update only'
              );

              // ✅ Close modal first, then refresh
              setShowEditLayoutModal(false);
              setEditingRecordId(null);
              setSelectedSlotDate(null);

              // ✅ Clear ALL events from UI
              console.log('🧹 Clearing ALL events from UI...');
              setCrmTasks([]);
              setGoogleEvents([]);
              setOutlookEvents([]);

              // ✅ Refresh CRM tasks immediately (DB is fast)
              setTimeout(async () => {
                console.log('🔄 Step 1: Refreshing CRM tasks (500ms)...');
                await fetchCrmTasks();
              }, 500);

              // ✅ Second refresh to ensure UI updates
              setTimeout(async () => {
                console.log('🔄 Step 2: Second CRM task refresh (1.5s)...');
                await fetchCrmTasks();
              }, 1500);
            }
          }
        }}
      />
    </div>
  );
};

export default CalendarComponent;
